import { cancelPendingAggregations } from '@pendo/agg-connect';
import { PendoNotification } from '@pendo/components';
import { InvitationError } from '@pendo/login';
import identity from 'lodash/identity';
import { createRouter, createWebHistory } from 'vue-router';
// store import to be addressed in APP-129700
// eslint-disable-next-line no-restricted-imports
import store from '@/vuex/store';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@middleware/constants/internal-performance-tracking';
import { SECONDARY_LEVEL_NAV_OPTIONS, TOP_LEVEL_NAV_OPTIONS } from '@/constants/nav';
import {
    clearFlags as clearLocalFlags,
    localSubscriptionFlagsExist,
    setSubscriptionFlags,
    SUBSCRIPTION_FLAG_STORAGE_KEY,
} from '@/data/flags/local-flags';
import { activeSubHasPurchasedProductLine } from '@/data/flags/product-lines.js';
import { getActiveSubscription } from '@/data/session/session-globals';
import { extractSubIdFromPath } from '@/utils/sub-id-routing.utils.js';
import { EXPLORE_STATUSES, getExploreStatus } from '@/voc/helpers/permissions.helper';
import {
    adminRoutesGuard,
    beforeEnterGuardRedirectByAfter,
    hasFeatureFlag,
    hasOrgTabsPermissions,
    hasProductLine,
    homeBeforeEnter,
} from '@/vue-router/route-guards.utils';
import * as RoutePermissions from '@/vue-router/route-permissions.utils';
import { persistedQueries } from '@/vue-router/route.utils';
import { updateActiveSubscription } from '@/vuex/modules/auth/auth.utils.js';
import { redirectLegacyRoutesGlobal, redirectLegacyRoutesWithSubId } from './preserved-redirect-routes.js';
import { initTitleContext, clearTitleContext, scheduleApplyTitle } from '@/vue-router/title-context.js';
import { i18n_t as t } from '@/utils/i18n';

/** Map of route name to countable type for "ask in agent mode" availability */
const AGENT_MODE_COUNTABLE_BY_ROUTE = {
    PageDetails: 'page',
    PageParameterDetails: 'page',
    FeatureDetails: 'feature',
    TrackEventsDetails: 'trackEvent',
    VisitorDetails: 'visitor',
    AccountDetails: 'account',
};

const subIdRoutes = [
    {
        path: '',
        name: 'Home',
        beforeEnter: homeBeforeEnter,
        meta: {
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.HOME,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasHomeRedirect(),
        },
    },
    {
        path: 'resource-center',
        component: () => import('@/resource-center/landing-page/resource-center-landing-page.vue'),
        props: true,
        meta: {
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasResourceCenterRedirect(),
        },
        children: [
            {
                path: '',
                name: 'ResourceCenter',
                component: () => import('@/resource-center/resource-center.vue'),
                meta: {
                    title: 'Resource Center',
                    prefixDomainTitle: false,
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('ResourceCenter'),
                    productArea: PRODUCT_AREAS.RESOURCE_CENTER,
                    subProductArea: SUB_PRODUCT_AREAS.RC_LIST,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Resource Center'),
                            params: {},
                            icon: { type: 'guide' },
                            keywords: ['guides'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.RESOURCE_CENTER,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasResourceCenterRedirect(),
                },
            },
            {
                path: ':originId/metrics',
                name: 'ResourceCenterMetrics',
                component: () => import('@/resource-center/metrics/resource-center-metrics.vue'),
                props: true,
                meta: {
                    showTabs: true,
                    title: 'Resource Center Metrics',
                    prefixDomainTitle: false,
                    activeTab: 'metrics',
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.RESOURCE_CENTER,
                    },
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('ResourceCenterMetrics'),
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasResourceCenterRedirect(),
                    productArea: PRODUCT_AREAS.RESOURCE_CENTER,
                    subProductArea: SUB_PRODUCT_AREAS.RC_METRICS,
                },
            },
            {
                path: ':originId/:publishedState',
                name: 'ResourceCenterSettings',
                component: () => import('@/resource-center/resource-center.vue'),
                props: true,
                meta: {
                    title: 'Resource Center Settings',
                    prefixDomainTitle: false,
                    showTabs: true,
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('ResourceCenterSettings'),
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasResourceCenterRedirect(),
                    productArea: PRODUCT_AREAS.RESOURCE_CENTER,
                    subProductArea: SUB_PRODUCT_AREAS.RC_DETAILS,
                    activeTab: 'settings',
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.RESOURCE_CENTER,
                    },
                },
            },
            {
                path: ':originId/:moduleRouteParam/:moduleId',
                name: 'EditModule',
                component: () => import('@/resource-center/edit-module/edit-module.vue'),
                props: true,
                meta: {
                    title: 'Edit Module',
                    prefixDomainTitle: false,
                    showTabs: false,
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.RESOURCE_CENTER,
                    },
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('EditModule'),
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasResourceCenterRedirect(),
                    productArea: PRODUCT_AREAS.RESOURCE_CENTER,
                    subProductArea: SUB_PRODUCT_AREAS.RC_MODULE_DETAILS,
                },
            },
        ],
    },
    {
        path: 'replay',
        name: 'SessionReplay',
        redirect: (to) => {
            return {
                name: 'ReplayLibrarySavedFilter',
                params: { ...to.params, savedFilterId: store.getters['savedFilters/lastSavedFilterId'] },
                query: to.query,
            };
        },
        meta: {
            title: 'Replay',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SessionReplay', { ignoreQuery: true }),
            productArea: PRODUCT_AREAS.SESSION_REPLAY,
            subProductArea: SUB_PRODUCT_AREAS.LIBRARY,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSessionReplayRedirect(),
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Replay library'),
                    params: {},
                    icon: { type: 'replay' },
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.REPLAY,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPLAY_LIBRARY,
            },
        },
    },
    {
        path: 'buy-replay',
        name: 'BuySessionReplay',
        component: () => import('@/session-replay/session-replay-upsell-page.vue'),
        meta: {
            title: 'Replay',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SessionReplay', { ignoreQuery: true }),
            productArea: PRODUCT_AREAS.SESSION_REPLAY,
            subProductArea: SUB_PRODUCT_AREAS.LIBRARY,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.REPLAY_UPSELL,
            },
        },
    },
    {
        path: 'buy-predict',
        name: 'BuyPredict',
        component: () => import('@/predict/predict-upsell.vue'),
        meta: {
            title: 'Predict',
            productArea: PRODUCT_AREAS.PREDICT,
            subProductArea: SUB_PRODUCT_AREAS.UPSELL,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasPredictUpsellRedirect(),
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PREDICT_UPSELL,
            },
        },
    },
    {
        path: 'replay/library/filters',
        name: 'ReplayLibrarySavedFilterList',
        component: () => import('@/session-replay/replay-library-saved-filters-list.vue'),
        meta: {
            title: 'Replay Library Saved Filters',
            productArea: PRODUCT_AREAS.SESSION_REPLAY,
            subProductArea: SUB_PRODUCT_AREAS.LIBRARY,
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SessionReplay', {
                ignoreQuery: true,
            }),
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Replay views list'),
                    params: {},
                    icon: { type: 'replay' },
                    keywords: ['library'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.REPLAY,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPLAY_LIBRARY,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSessionReplayRedirect(),
        },
    },
    {
        path: 'replay/library/filters/:savedFilterId',
        name: 'ReplayLibrarySavedFilter',
        props: true,
        component: () => import('@/session-replay/replay-library-saved-filter.vue'),
        meta: {
            title: 'Replay Library saved filter',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.REPLAY,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPLAY_LIBRARY,
            },
            productArea: PRODUCT_AREAS.SESSION_REPLAY,
            subProductArea: SUB_PRODUCT_AREAS.LIBRARY,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSessionReplayRedirect(),
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SessionReplay', {
                ignoreQuery: true,
            }),
        },
    },
    {
        path: 'replay/player/:recordingSessionId',
        name: 'SessionReplayRecording',
        component: () => import('@/session-replay/session-replay-detail.vue'),
        meta: {
            title: 'Replay',
            hideFooter: true,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.REPLAY,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPLAY_LIBRARY,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSessionReplayRedirect(),
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SessionReplay', { ignoreQuery: true }),
            productArea: PRODUCT_AREAS.SESSION_REPLAY,
            subProductArea: SUB_PRODUCT_AREAS.PLAYER,
        },
    },
    {
        path: 'replay/clips',
        name: 'SavedClips',
        component: () => import('@/session-replay/saved-clips.vue'),
        meta: {
            title: 'Saved Clips',
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSessionReplayRedirect(),
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SessionReplay', { ignoreQuery: true }),
            productArea: PRODUCT_AREAS.SESSION_REPLAY,
            subProductArea: SUB_PRODUCT_AREAS.PLAYER,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Saved clips'),
                    params: {},
                    icon: { type: 'replay' },
                    keywords: ['replay'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.REPLAY,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.SAVED_CLIPS,
            },
        },
    },
    {
        path: 'replay/clips/:tab',
        name: 'SavedClipsTab',
        component: () => import('@/session-replay/saved-clips.vue'),
        meta: {
            title: 'Saved Clips',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SessionReplay', { ignoreQuery: true }),
            productArea: PRODUCT_AREAS.SESSION_REPLAY,
            subProductArea: SUB_PRODUCT_AREAS.PLAYER,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Saved playlists'),
                    params: { tab: 'playlists' },
                    icon: { type: 'replay' },
                    keywords: ['replay'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.REPLAY,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.SAVED_CLIPS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSessionReplayRedirect(),
        },
    },
    {
        path: 'replay/clip/saved/:id',
        name: 'SavedClipRecording',
        component: () => import('@/session-replay/saved-clip-detail.vue'),
        meta: {
            title: 'Saved Clips',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.REPLAY,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.SAVED_CLIPS,
            },
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SessionReplay', { ignoreQuery: true }),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSessionReplayRedirect(),
            productArea: PRODUCT_AREAS.SESSION_REPLAY,
            subProductArea: SUB_PRODUCT_AREAS.PLAYER,
            hideFooter: true,
        },
    },
    {
        path: 'replay/clips/playlists/:id',
        name: 'SavedClipsPlaylistDetails',
        component: () => import('@/session-replay/saved-clips-playlist-details.vue'),
        meta: {
            title: 'Saved Clips Playlist Details',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.REPLAY,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.SAVED_CLIPS,
            },
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SessionReplay', { ignoreQuery: true }),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSessionReplayRedirect(),
            productArea: PRODUCT_AREAS.SESSION_REPLAY,
            subProductArea: SUB_PRODUCT_AREAS.LIBRARY,
        },
    },
    {
        path: 'emails',
        name: 'Emails',
        component: () => import('@/emails/pages/landing-page/landing-page.vue'),
        meta: {
            title: 'Emails',
            productArea: PRODUCT_AREAS.ORCHESTRATE,
            subProductArea: SUB_PRODUCT_AREAS.EMAIL,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Emails'),
                    params: {},
                    icon: { type: 'orchestrate' },
                    keywords: ['orchestrate'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ORCHESTRATE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.EMAILS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasStandaloneEmailsRedirect(),
        },
    },
    {
        path: 'emails/:id',
        name: 'EmailDetails',
        component: () => import('@/emails/pages/email-details/email-details.vue'),
        props: true,
        meta: {
            title: 'Email Details',
            productArea: PRODUCT_AREAS.ORCHESTRATE,
            subProductArea: SUB_PRODUCT_AREAS.EMAIL,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ORCHESTRATE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.EMAILS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasStandaloneEmailsRedirect(),
        },
    },
    {
        path: 'guides',
        children: [
            {
                path: '',
                name: 'Guides',
                component: () => import('@/guides/guides-landing-page/guides-landing-page.vue'),
                props (route) {
                    return {
                        openChooser: route.query.openChooser,
                    };
                },
                meta: {
                    title: 'Guides',
                    prefixDomainTitle: false,
                    productArea: PRODUCT_AREAS.GUIDES,
                    subProductArea: SUB_PRODUCT_AREAS.GUIDE_LIST,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Guides'),
                            params: {},
                            icon: { type: 'guide' },
                        },
                    ],
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasBasicGuidesRedirect(),
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('Guides'),
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.GUIDES,
                    },
                },
            },
            {
                path: 'conversational-ai/:appId/:conversationId/:template?',
                name: 'GuidesConversationalAiPage',
                component: () => import('@/guides/guides-conversational-ai/guides-conversational-ai-page.vue'),
                props (route) {
                    return {
                        guideId: route.query.guideId,
                        conversationId: route.params.conversationId,
                        appId: route.params.appId,
                        template: route.params.template,
                        experimentId: route.query.experimentId,
                        experimentVariant: route.query.experimentVariant,
                    };
                },
                meta: {
                    title: 'Guides Conversational AI',
                    prefixDomainTitle: false,
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.GUIDES,
                    },
                    productArea: PRODUCT_AREAS.GUIDES,
                    subProductArea: SUB_PRODUCT_AREAS.AI_AGENTS,
                    hideFooter: true,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasBasicGuidesRedirect(),
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('Guides'),
                },
            },
            {
                path: 'alerts',
                name: 'GuideAlerts',
                component: () => import('@/guides/guides-landing-page/guides-landing-page.vue'),
                meta: {
                    title: 'Guide Alerts',
                    prefixDomainTitle: false,
                    productArea: PRODUCT_AREAS.GUIDES,
                    subProductArea: SUB_PRODUCT_AREAS.GUIDE_DISPLAY_ALERTS,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Guide alerts'),
                            params: {},
                            icon: { type: 'guide' },
                            keywords: ['guides'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.GUIDES,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasBasicGuidesRedirect(),
                },
            },
            {
                path: 'ordering',
                name: 'GuideOrdering',
                component: () => import('@/guides/guides-landing-page/guides-landing-page.vue'),
                meta: {
                    title: 'Guide Ordering',
                    prefixDomainTitle: false,
                    productArea: PRODUCT_AREAS.GUIDES,
                    subProductArea: SUB_PRODUCT_AREAS.GUIDE_ORDERING,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Guide ordering'),
                            params: {},
                            icon: { type: 'guide' },
                            keywords: ['guides'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.GUIDES,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasGuidesOrderingRedirect(),
                },
            },
            {
                path: 'audit',
                name: 'GuideAuditLog',
                component: () => import('@/guides/guides-landing-page/guides-landing-page.vue'),
                meta: {
                    title: 'Guide Audit Log',
                    prefixDomainTitle: false,
                    productArea: PRODUCT_AREAS.GUIDES,
                    subProductArea: SUB_PRODUCT_AREAS.GUIDE_LIST,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Guide audit'),
                            params: {},
                            icon: { type: 'guide' },
                            keywords: ['guides'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.GUIDES,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasGuideAuditRedirect(),
                },
            },
            {
                path: 'experiments',
                name: 'GuideExperiments',
                component: () => import('@/guides/guides-landing-page/guides-landing-page.vue'),
                meta: {
                    title: 'Guide Experiments',
                    prefixDomainTitle: false,
                    productArea: PRODUCT_AREAS.GUIDES,
                    subProductArea: SUB_PRODUCT_AREAS.GUIDE_EXPERIMENTS,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasGuideExperimentsRedirect(),
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.GUIDES,
                    },
                },
            },
            {
                path: ':guideId',
                name: 'GuideDetails',
                component: () => import('@/guides/p2-guide-details/guide-details.vue'),
                meta: {
                    title: 'Guide Details',
                    prefixDomainTitle: false,
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.GUIDES,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasBasicGuidesRedirect(),
                },
                props: true,
            },
        ],
    },
    {
        path: 'guides-pro/learn-more',
        name: 'GuidesProUpsell',
        component: () => import('@/guides/guides-upsell-page/guides-pro-upsell-page.vue'),
        meta: {
            title: 'Guides Pro',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES_PRO_UPSELL,
            },
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Guides'),
            productArea: PRODUCT_AREAS.GUIDES,
            subProductArea: SUB_PRODUCT_AREAS.GUIDE_UPSELL,
        },
    },
    {
        path: 'guides-basic/learn-more',
        name: 'GuidesBasicUpsell',
        component: () => import('@/guides/guides-upsell-page/guides-basic-upsell-page.vue'),
        meta: {
            title: 'Guides',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES_UPSELL,
            },
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Guides'),
            productArea: PRODUCT_AREAS.GUIDES,
            subProductArea: SUB_PRODUCT_AREAS.GUIDE_UPSELL,
        },
    },
    {
        path: 'forms',
        name: 'Forms',
        component: () => import('@/forms/forms.vue'),
        props: true,
        meta: {
            title: 'Form validation',
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Form validation'),
                    params: {},
                    icon: { type: 'guide' },
                    keywords: ['guides'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.FORM_VALIDATION,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasFormValidationRedirect(),
        },
    },
    {
        path: 'forms/:id',
        name: 'FormDetails',
        component: () => import('@/forms/form-details.vue'),
        props: true,
        meta: {
            title: 'Form detail',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.FORM_VALIDATION,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasFormValidationRedirect(),
        },
    },
    {
        path: 'listen',
        component: () => import('@/voc/pages/index.vue'),
        meta: {
            title: 'Listen',
            productArea: PRODUCT_AREAS.VOC,
            prefixDomainTitle: false,
        },
        children: [
            {
                path: '',
                name: 'ListenGetStarted',
                component: () => import('@/voc/pages/onboarding/onboarding.vue'),
                meta: {
                    title: 'Introducing Listen',
                    prefixDomainTitle: false,
                    activeNavOption: {
                        topOptionName: () => {
                            return hasProductLine('voc', 'feedback') || hasProductLine('voc', 'ideas')
                                ? TOP_LEVEL_NAV_OPTIONS.LISTEN
                                : TOP_LEVEL_NAV_OPTIONS.LISTEN_UPSELL;
                        },
                        secondaryOptionName: () => {
                            return hasProductLine('voc', 'feedback') || hasProductLine('voc', 'ideas')
                                ? SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_GETTING_STARTED
                                : '';
                        },
                    },
                },
            },
            {
                path: 'feedback',
                component: () => import('@/voc/pages/feedback/feedback.vue'),
                meta: {
                    title: 'Feedback - Listen',
                    prefixDomainTitle: false,
                },
                children: [
                    {
                        path: ':itemId?',
                        name: 'ListenFeedbackAllFeedbackView',
                        component: () => import('@/voc/pages/feedback/views/view/explore-view.vue'),
                        meta: {
                            title: 'Feedback - Listen',
                            productArea: PRODUCT_AREAS.VOC,
                            subProductArea: SUB_PRODUCT_AREAS.CAPTURE,
                            prefixDomainTitle: false,
                            activeNavOption: {
                                topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_FEEDBACK,
                            },
                            navigationQuickLinks: [
                                {
                                    label: ($t) => $t('jumpTo.All feedback'),
                                    params: {},
                                    icon: { type: 'listen' },
                                },
                            ],
                            hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenAllFeedbackRedirect(),
                        },
                    },
                    {
                        path: 'form',
                        name: 'ValidateFeedbackFormExternal',
                        component: () => import('@/voc/components/voc-feedback-form/voc-feedback-form.vue'),
                        meta: {
                            title: 'Feedback form - Listen',
                            activeNavOption: {
                                topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_FEEDBACK,
                            },
                            productArea: PRODUCT_AREAS.VOC,
                            subProductArea: SUB_PRODUCT_AREAS.CAPTURE,
                            hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenFeedbackRedirect(),
                            prefixDomainTitle: false,
                        },
                    },
                    {
                        path: 'explore',
                        name: 'ListenFeedbackExplore',
                        component: () => import('@/voc/pages/feedback/explore-page/explore-page.vue'),
                        meta: {
                            title: 'Explore - Feedback - Listen',
                            productArea: PRODUCT_AREAS.VOC,
                            subProductArea: SUB_PRODUCT_AREAS.EXPLORE,
                            prefixDomainTitle: false,
                            parentName: 'ValidateFeedbackSavedViews',
                            navigationQuickLinks: [
                                {
                                    label: ($t) => $t('jumpTo.Explore feedback'),
                                    params: {},
                                    icon: { type: 'listen' },
                                    keywords: ['listen'],
                                },
                            ],
                            activeNavOption: {
                                topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.EXPLORE_FEEDBACK,
                            },
                            hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenExploreRedirect(),
                        },
                        beforeEnter (to, from, next) {
                            if (getExploreStatus() === EXPLORE_STATUSES.READY) {
                                next();

                                return;
                            }

                            next('/listen/feedback/explore-overview');
                        },
                    },
                    {
                        path: 'explore-overview',
                        name: 'ListenFeedbackExploreOverview',
                        component: () => import('@/voc/pages/feedback/explore-overview/explore-overview.vue'),
                        meta: {
                            title: 'Explore Overview - Feedback - Listen',
                            activeNavOption: {
                                topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.EXPLORE_FEEDBACK,
                            },
                            productArea: PRODUCT_AREAS.VOC,
                            subProductArea: SUB_PRODUCT_AREAS.CAPTURE,
                            prefixDomainTitle: false,
                            hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenExploreRedirect(),
                        },
                        beforeEnter (to, from, next) {
                            if (getExploreStatus() === EXPLORE_STATUSES.READY) {
                                next('/listen/feedback/explore');

                                return;
                            }
                            next();
                        },
                    },
                    {
                        path: 'views',
                        component: () => import('@/voc/pages/feedback/views/views.vue'),
                        meta: {
                            title: 'Feedback views - Listen',
                            prefixDomainTitle: false,
                        },
                        children: [
                            {
                                path: '',
                                name: 'ValidateFeedbackSavedViews',
                                component: () => import('@/voc/pages/feedback/views/views-list/views-list.vue'),
                                meta: {
                                    title: 'Feedback views - Listen',
                                    productArea: PRODUCT_AREAS.VOC,
                                    subProductArea: SUB_PRODUCT_AREAS.CAPTURE,
                                    prefixDomainTitle: false,
                                    navigationQuickLinks: [
                                        {
                                            label: ($t) => $t('jumpTo.Feedback views'),
                                            params: {},
                                            icon: { type: 'listen' },
                                            keywords: ['listen'],
                                        },
                                    ],
                                    activeNavOption: {
                                        topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_FEEDBACK,
                                    },
                                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenFeedbackRedirect(),
                                },
                            },
                            {
                                path: 'explore/:tab?',
                                name: 'ListenFeedbackExploreTabs',
                                component: () => import('@/voc/pages/feedback/views/view/explore-view.vue'),
                                meta: {
                                    title: 'Explore - Feedback - Listen',
                                    prefixDomainTitle: false,
                                    parentName: 'ValidateFeedbackSavedViews',
                                    productArea: PRODUCT_AREAS.VOC,
                                    subProductArea: SUB_PRODUCT_AREAS.CAPTURE,
                                    switchSubscriptionToRoute: switchSubscriptionToRouteName(
                                        'ValidateFeedbackSavedViews',
                                        { ignoreQuery: true },
                                    ),
                                    activeNavOption: {
                                        topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.EXPLORE_FEEDBACK,
                                    },
                                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenExploreRedirect(),
                                },
                                beforeEnter (to, from, next) {
                                    if (getExploreStatus() === EXPLORE_STATUSES.READY) {
                                        next();

                                        return;
                                    }

                                    next('/listen/feedback/explore-overview');
                                },
                            },
                            {
                                path: ':savedviewId/:tab?/:version?',
                                name: 'ValidateFeedbackSavedViewTabs',
                                component: () => import('@/voc/pages/feedback/views/view/explore-view.vue'),
                                meta: {
                                    title: 'Feedback - Listen',
                                    productArea: PRODUCT_AREAS.VOC,
                                    subProductArea: SUB_PRODUCT_AREAS.CAPTURE,
                                    switchSubscriptionToRoute: switchSubscriptionToRouteName(
                                        'ValidateFeedbackSavedViews',
                                        { ignoreQuery: true },
                                    ),
                                    prefixDomainTitle: false,
                                    parentName: 'ValidateFeedbackSavedViews',
                                    activeNavOption: {
                                        topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_FEEDBACK,
                                    },
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'freemium',
                name: 'ListenFreemium',
                component: () => import('@/voc/pages/freemium/freemium.vue'),
                meta: {
                    title: 'Freemium - Listen',
                    productArea: PRODUCT_AREAS.VOC,
                    subProductArea: SUB_PRODUCT_AREAS.GENERAL,
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                    },
                    prefixDomainTitle: false,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenFreemiumRedirect(),
                },
            },
            {
                path: 'settings',
                name: 'ListenSettings',
                component: () => import('@/voc/pages/setup/setup.vue'),
                meta: {
                    title: 'Setup - Listen',
                    prefixDomainTitle: false,
                    productArea: PRODUCT_AREAS.VOC,
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_SETTINGS,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenSettingsRedirect(),
                },
                children: [
                    {
                        path: 'statuses',
                        name: 'ListenSettingsCustomStatuses',
                        component: () => import('@/voc/pages/setup/custom-statuses/custom-statuses.vue'),
                        meta: {
                            title: 'Statuses - Listen settings',
                            prefixDomainTitle: false,
                            productArea: PRODUCT_AREAS.VOC,
                            activeNavOption: {
                                topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_SETTINGS,
                            },
                            navigationQuickLinks: [
                                {
                                    label: ($t) => $t('jumpTo.Listen statuses'),
                                    params: {},
                                    icon: { type: 'listen' },
                                    keywords: ['listen', 'custom', 'status', 'statuses'],
                                },
                            ],
                            hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenStatusesRedirect(),
                        },
                    },
                    {
                        path: 'automationrules',
                        name: 'ListenSettingsAutomationRules',
                        component: () => import('@/voc/pages/setup/automation-rules/automation-rules.vue'),
                        meta: {
                            title: 'Automation rules - Listen settings',
                            prefixDomainTitle: false,
                            productArea: PRODUCT_AREAS.VOC,
                            activeNavOption: {
                                topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_SETTINGS,
                            },
                            navigationQuickLinks: [
                                {
                                    label: ($t) => $t('jumpTo.Automation rules'),
                                    params: {},
                                    icon: { type: 'listen' },
                                    keywords: ['listen', 'automation', 'rules'],
                                },
                            ],
                        },
                    },
                    {
                        path: 'sources',
                        name: 'ListenSettingsConnectSources',
                        component: () => import('@/voc/pages/setup/connect-sources/connect-sources.vue'),
                        meta: {
                            title: 'Feedback sources - Listen settings',
                            prefixDomainTitle: false,
                            productArea: PRODUCT_AREAS.VOC,
                            activeNavOption: {
                                topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_SETTINGS,
                            },
                            navigationQuickLinks: [
                                {
                                    label: ($t) => $t('jumpTo.Feedback sources'),
                                    params: {},
                                    icon: { type: 'listen' },
                                    keywords: ['listen', 'feedback', 'sources'],
                                },
                            ],
                            hasPermissionsDeniedRedirect: () => RoutePermissions.hasFeedbackSourcesRedirect(),
                        },
                    },
                    {
                        path: 'templates',
                        name: 'ListenSettingsTemplates',
                        component: () => import('@/voc/pages/setup/manage-templates/manage-templates.vue'),
                        meta: {
                            title: 'Templates - Listen settings',
                            prefixDomainTitle: false,
                            productArea: PRODUCT_AREAS.VOC,
                            activeNavOption: {
                                topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_SETTINGS,
                            },
                            navigationQuickLinks: [
                                {
                                    label: ($t) => $t('jumpTo.Listen email templates'),
                                    params: {},
                                    icon: { type: 'listen' },
                                    keywords: ['listen', 'templates'],
                                },
                            ],
                            hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenSettingsTemplatesRedirect(),
                        },
                    },
                    {
                        path: 'jira',
                        name: 'ListenSettingsJiraConnect',
                        component: () => import('@/voc/pages/setup/jira/jira.vue'),
                        meta: {
                            title: 'Jira - Listen settings',
                            prefixDomainTitle: false,
                            productArea: PRODUCT_AREAS.VOC,
                            activeNavOption: {
                                topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_SETTINGS,
                            },
                            navigationQuickLinks: [
                                {
                                    label: ($t) => $t('jumpTo.Listen jira'),
                                    params: {},
                                    icon: { type: 'listen' },
                                    keywords: ['listen', 'jira'],
                                },
                            ],
                            hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenSettingsJiraConnectRedirect(),
                        },
                    },
                ],
            },
            {
                path: 'validate/:tab?',
                name: 'Validate',
                component: () => import('@/voc/pages/validate/validate.vue'),
                props: (route) => ({
                    tab: route.params.tab || 'ideas',
                }),
                meta: {
                    title: 'Validate - Listen',
                    productArea: PRODUCT_AREAS.VOC,
                    subProductArea: SUB_PRODUCT_AREAS.VALIDATE,
                    prefixDomainTitle: false,
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.VALIDATE_IDEAS,
                    },
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Validate ideas'),
                            params: { tab: '' },
                            icon: { type: 'listen' },
                            keywords: ['listen'],
                        },
                        {
                            label: ($t) => $t('jumpTo.Idea tests'),
                            params: { tab: 'ideatests' },
                            icon: { type: 'listen' },
                            keywords: ['listen', 'validate', 'ideas'],
                        },
                        {
                            label: ($t) => $t('jumpTo.Idea analysis'),
                            params: { tab: 'analysis' },
                            icon: { type: 'listen' },
                            keywords: ['listen', 'validate', 'ideas'],
                        },
                        {
                            label: ($t) => $t('jumpTo.Validate ROI'),
                            params: { tab: 'roi' },
                            icon: { type: 'listen' },
                            keywords: ['listen', 'ideas'],
                        },
                    ],
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasValidateRedirect(),
                },
            },
            {
                path: 'validate/ideatests/:guideId',
                name: 'IdeaTestDetails',
                component: () => import('@/guides/p2-guide-details/guide-details.vue'),
                props: true,
                meta: {
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.VALIDATE_IDEAS,
                    },
                },
            },
            {
                path: 'portals',
                name: 'VocPortals',
                children: [
                    {
                        path: '',
                        name: 'VocPortalsList',
                        component: () => import('@/voc/pages/setup/portals/portals-list/portals-list.vue'),
                        meta: {
                            title: 'Portals - Listen',
                            productArea: PRODUCT_AREAS.VOC,
                            prefixDomainTitle: false,
                            navigationQuickLinks: [
                                {
                                    label: ($t) => $t('jumpTo.Portals'),
                                    params: {},
                                    icon: { type: 'listen' },
                                },
                            ],
                            activeNavOption: {
                                topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_PORTAL,
                            },
                            hasPermissionsDeniedRedirect: () => RoutePermissions.hasValidateRedirect(),
                        },
                    },
                    {
                        path: 'settings/:portalId',
                        name: 'VocPortalSettingsTab',
                        component: () => import('@/voc/pages/setup/portals/settings/settings.vue'),
                        meta: {
                            title: 'Portal - Settings',
                            prefixDomainTitle: false,
                            activeNavOption: {
                                topOptionName: TOP_LEVEL_NAV_OPTIONS.LISTEN,
                                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.LISTEN_PORTAL,
                            },
                            productArea: PRODUCT_AREAS.VOC,
                            hasPermissionsDeniedRedirect: () => RoutePermissions.hasValidateRedirect(),
                        },
                    },
                ],
            },
        ],
    },
    {
        path: 'buy-salesforce',
        name: 'SalesforceUpsell',
        component: () => import('@/salesforce/salesforce-upsell-page.vue'),
        meta: {
            title: 'Salesforce Integration',
            productArea: PRODUCT_AREAS.INTEGRATIONS,
            subProductArea: SUB_PRODUCT_AREAS.UPSELL,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSalesforceUpsellRedirect(),
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SALESFORCE_UPSELL,
            },
        },
    },
    {
        path: 'themes',
        name: 'Themes',
        component: () => import('@/themes/themes.vue'),
        meta: {
            title: 'Themes',
            subProductArea: SUB_PRODUCT_AREAS.THEMES,
            productArea: PRODUCT_AREAS.GUIDES,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Themes'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['guides'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasThemesRedirect(),
        },
    },
    {
        path: 'experiments',
        name: 'ExperimentsList',
        component: () => import('@/guides/p2-guide-details/guide-ab-testing/experiments-list.vue'),
        meta: {
            title: 'Experiments',
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Experiments'),
                    params: {},
                    icon: { type: 'guide' },
                    keywords: ['guides'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.EXPERIMENTS,
            },
            productArea: PRODUCT_AREAS.GUIDES,
            subProductArea: SUB_PRODUCT_AREAS.GUIDE_EXPERIMENTS,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAbTestingRedirect(),
        },
    },
    {
        path: 'experiments/:id',
        name: 'ExperimentDetails',
        component: () => import('@/guides/p2-guide-details/guide-ab-testing/experiment-details.vue'),
        props: true,
        meta: {
            title: 'Experiment Details',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.GUIDES,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.EXPERIMENTS,
            },
            productArea: PRODUCT_AREAS.GUIDES,
            subProductArea: SUB_PRODUCT_AREAS.GUIDE_EXPERIMENTS,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAbTestingRedirect(),
        },
    },
    {
        path: 'portfolio-summary',
        name: 'PortfolioSummary',
        component: () => import('@/portfolio-summary/portfolio-summary.vue'),
        meta: {
            title: 'Portfolio Summary',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.PORTFOLIO_SUMMARY,
            },
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.PORTFOLIO_OVERVIEW,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasPortfolioSummaryRedirect(),
        },
    },
    {
        path: 'command-center',
        name: 'PortfolioInsights',
        component: () => import('@/portfolio-insights/portfolio-insights.vue'),
        meta: {
            title: 'Command Center',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.PORTFOLIO_INSIGHTS,
            },
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.PORTFOLIO_OVERVIEW,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasPortfolioInsightsRedirect(),
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Command Center'),
                    params: {},
                    icon: { type: 'layout' },
                },
            ],
        },
    },
    {
        path: 'analytics',
        name: 'AnalyticsUpsell',
        component: () => import('@/analytics/analytics-upsell-page/analytics-upsell-page.vue'),
        meta: {
            title: 'Analytics',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS_UPSELL,
            },
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.UPSELL,
        },
    },
    {
        path: 'agent-analytics',
        name: 'AiAgents',
        component: () => import('@/ai-agents/components/ai-agents-list.vue'),
        props: (route) => ({
            aiAgentId: route.query.aiAgentId,
        }),
        meta: {
            title: 'Agent Analytics',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.AI_AGENTS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Agent Analytics'),
                    params: {},
                    icon: { type: 'layout' },
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.AI_AGENTS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAiAgentsRedirect(),
        },
    },
    {
        path: 'agent-analytics/report/:reportId?',
        name: 'AiAgentReport',
        component: () => import('@/ai-agents/components/ai-agent-report.vue'),
        props: (route) => ({
            reportId: route.params.reportId,
            aiAgentIdFromRoute: route.query.aiAgentId,
            conversationIdFromRoute: route.query.conversationId,
            dateTypeFromRoute: route.query.dateType,
            startDateFromRoute: route.query.start ?? route.query.startDate,
            endDateFromRoute: route.query.end ?? route.query.endDate,
            messageIdFromRoute: route.query.messageId,
            sourceTabFromRoute: route.query.sourceTab,
            sourceTableTypeFromRoute: route.query.sourceTableType,
            sourceTypeFromRoute: route.query.sourceType,
            segmentIdFromRoute: route.query.segmentId,
            sourceClusterIdFromRoute: route.query.sourceClusterId,
            sourceClusterNameFromRoute: route.query.sourceClusterName,
            sourceRowIdFromRoute: route.query.sourceRowId,
            promptEventIdFromRoute: route.query.promptEventId,
            promptVisitorIdFromRoute: route.query.promptVisitorId,
            promptBrowserTimeFromRoute: route.query.promptBrowserTime,
        }),
        meta: {
            title: 'AI Agent Report',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.AI_AGENTS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAiAgentsRedirect(),
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.AI_AGENTS,
        },
    },
    {
        path: 'reports',
        name: 'SavedReports',
        component: () => import('@/reports/components/saved-reports/saved-reports.vue'),
        meta: {
            title: 'Saved Reports',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.REPORTS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Saved reports'),
                    params: {},
                    icon: { type: 'bar-chart-big' },
                    keywords: ['analytics'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasBasicAnalyticsRedirect(),
        },
    },
    {
        path: 'recommendations-insights',
        name: 'Insights',
        component: () => import('@/insights/views/insights-management/insights-management.vue'),
        meta: {
            title: 'Insights',
            productArea: PRODUCT_AREAS.ORCHESTRATE,
            subProductArea: SUB_PRODUCT_AREAS.INSIGHTS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Insights'),
                    params: {},
                    icon: { type: 'bar-chart-big' },
                    keywords: ['analytics'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.INSIGHTS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasInsightsRedirect(),
        },
        children: [
            {
                path: ':id',
                name: 'InsightDetails',
                meta: {
                    title: 'Insight Details',
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.INSIGHTS,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasInsightsRedirect(),
                    productArea: PRODUCT_AREAS.ORCHESTRATE,
                    subProductArea: SUB_PRODUCT_AREAS.INSIGHTS,
                },
            },
        ],
    },
    {
        path: 'data-explorer/:id?',
        name: 'DataExplorer',
        component: () => import('@/data-explorer/data-explorer.vue'),
        props: true,
        meta: {
            title: 'Data Explorer',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.DATA_EXPLORER,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasDataExplorerAccessRedirect(),
        },
    },
    {
        path: 'data-explorer-reports',
        name: 'DataExplorerList',
        component: () => import('@/data-explorer/reports/data-explorer-list.vue'),
        props: true,
        meta: {
            title: 'Data Explorer Reports',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.DATA_EXPLORER,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Data Explorer reports'),
                    params: {},
                    icon: { type: 'bar-chart-big' },
                    keywords: ['analytics', 'dx'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasDataExplorerAccessRedirect(),
        },
    },
    {
        path: 'features',
        component: () => import('@/features/index.vue'),
        children: [
            {
                path: '',
                name: 'Features',
                component: () => import('@/features/features-list.vue'),
                meta: {
                    title: 'Features',
                    productArea: PRODUCT_AREAS.ANALYTICS,
                    subProductArea: SUB_PRODUCT_AREAS.FEATURE_LIST_DETAILS,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasOEMPagesAndFeaturesRedirect(),
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Features'),
                            params: {},
                            icon: { type: 'layout' },
                            keywords: ['product'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.FEATURES,
                    },
                },
            },
            {
                path: 'alerts',
                name: 'FeatureAlerts',
                component: () => import('@/features/features-list.vue'),
                meta: {
                    title: 'Feature Alerts',
                    productArea: PRODUCT_AREAS.MOBILE,
                    subProductArea: SUB_PRODUCT_AREAS.ALERTS,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Feature alerts'),
                            params: {},
                            icon: { type: 'layout' },
                            keywords: ['product', 'features'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.FEATURES,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasFeatureAlertsRedirect(),
                },
            },
            {
                path: ':featureId',
                name: 'FeatureDetails',
                component: () => import('@/features/feature-details/feature-details.vue'),
                meta: {
                    title: 'Feature Details',
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('Features'),
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.FEATURES,
                    },
                    agentModeCountableType: AGENT_MODE_COUNTABLE_BY_ROUTE.FeatureDetails,
                },
            },
        ],
    },
    {
        path: 'tag-maintenance',
        name: 'TagMaintenance',
        component: () => import('@/tag-maintenance/tag-maintenance.vue'),
        meta: {
            title: 'Tag maintenance',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.TAG_MAINTENANCE,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Tag maintenance'),
                    params: {},
                    icon: { type: 'settings' },
                },
            ],
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasTagMaintenanceRedirect(),
        },
    },
    {
        path: 'segments',
        name: 'Segments',
        component: () => import('@/segments-page/segments-page.vue'),
        meta: {
            title: 'Segments',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.SEGMENTS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Segments'),
                    params: {},
                    icon: { type: 'users' },
                    keywords: ['people'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.SEGMENTS,
            },
        },
    },
    {
        path: 'web-analytics',
        name: 'WebAnalytics',
        component: () => import('@/web-analytics/web-analytics.vue'),
        meta: {
            title: 'Web analytics',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.WEB_ANALYTICS,
            hideFooter: true,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Web analytics'),
                    params: {},
                    icon: { type: 'bar-chart-big' },
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.WEB_ANALYTICS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasWebAnalyticsRedirect(),
        },
    },
    {
        path: 'apps',
        name: 'Apps',
        component: () => import('@/app-health/app-health.vue'),
        meta: {
            title: 'Apps',
            productArea: PRODUCT_AREAS.ANALYTICS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.APPS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAppHealthRedirect(),
        },
    },
    {
        path: 'apps/:appId',
        name: 'AppDetails',
        component: () => import('@/app-health/app-details.vue'),
        meta: {
            title: 'App Details',
            productArea: PRODUCT_AREAS.ANALYTICS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.APPS,
            },
            hasPermissionsDeniedRedirect: (to) => RoutePermissions.hasAppDetailsRedirect(to),
        },
    },
    {
        path: 'metadata',
        name: 'Metadata',
        component: () => import('@/metadata/metadata-details/metadata-details.vue'),
        meta: {
            title: 'Metadata',
            productArea: PRODUCT_AREAS.METADATA,
            subProductArea: SUB_PRODUCT_AREAS.METADATA,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Metadata analytics'),
                    params: {},
                    icon: { type: 'users' },
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasMetadataAnalyticsRedirect(),
        },
    },
    {
        path: 'product-areas-list',
        name: 'ProductAreas',
        component: () => import('@/groups/groups.vue'),
        meta: {
            title: 'Product Areas',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.PRODUCT_AREAS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Product Areas'),
                    params: {},
                    icon: { type: 'layout' },
                    keywords: ['product'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.PRODUCT_AREAS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasProductAreasRedirect(),
        },
    },
    {
        path: 'product-areas/:id',
        name: 'ProductAreaDetails',
        component: () => import('@/groups/group-details/group-details.vue'),
        props: true,
        meta: {
            title: 'Product Area Details',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.PRODUCT_AREAS,
            },
            switchSubscriptionToRoute: switchSubscriptionToRouteName('ProductAreas'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasProductAreasRedirect(),
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.PRODUCT_AREAS,
        },
    },
    {
        path: 'pages',
        component: () => import('@/pages/index.vue'),
        children: [
            {
                path: '',
                name: 'Pages',
                component: () => import('@/pages/pages.vue'),
                meta: {
                    title: 'Pages',
                    productArea: PRODUCT_AREAS.ANALYTICS,
                    subProductArea: SUB_PRODUCT_AREAS.PAGE_LIST_DETAILS,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasOEMPagesAndFeaturesRedirect(),
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Pages'),
                            params: {},
                            icon: { type: 'layout' },
                            keywords: ['product'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.PAGES,
                    },
                },
            },
            {
                path: 'untagged',
                name: 'UntaggedURLs',
                component: () => import('@/pages/pages.vue'),
                meta: {
                    title: 'Untagged URLs',
                    productArea: PRODUCT_AREAS.TAGGING_AND_SETUP,
                    subProductArea: SUB_PRODUCT_AREAS.UNTAGGED_URLS,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Untagged URLs'),
                            params: {},
                            icon: { type: 'layout' },
                            keywords: ['product', 'pages', 'untagged'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.PAGES,
                    },
                },
            },
            {
                path: 'alerts',
                name: 'PageAlerts',
                component: () => import('@/pages/pages.vue'),
                meta: {
                    title: 'Page Alerts',
                    productArea: PRODUCT_AREAS.MOBILE,
                    subProductArea: SUB_PRODUCT_AREAS.ALERTS,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Page alerts'),
                            params: {},
                            icon: { type: 'layout' },
                            keywords: ['product', 'pages'],
                        },
                    ],
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasPageAlertsRedirect(),
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.PAGES,
                    },
                },
            },
            {
                path: ':pageId',
                name: 'PageDetails',
                component: () => import('@/pages/page-details/page-details.vue'),
                meta: {
                    title: 'Page Details',
                    productArea: PRODUCT_AREAS.ANALYTICS,
                    subProductArea: SUB_PRODUCT_AREAS.PAGE_LIST_DETAILS,
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('Pages'),
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.PAGES,
                    },
                    agentModeCountableType: AGENT_MODE_COUNTABLE_BY_ROUTE.PageDetails,
                },
            },
            {
                path: ':pageId/parameters/:parameterId',
                name: 'PageParameterDetails',
                component: () => import('@/pages/page-details/page-details.vue'),
                meta: {
                    title: 'Page Parameter Details',
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('Pages'),
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.PAGES,
                    },
                    agentModeCountableType: AGENT_MODE_COUNTABLE_BY_ROUTE.PageParameterDetails,
                },
            },
        ],
    },
    {
        path: 'admin/buy-now',
        name: 'BuyNow',
        component: () => import('@/free/buy-now/buy-now.vue'),
        meta: {
            title: 'Buy Now',
            productArea: PRODUCT_AREAS.GROWTH,
            subProductArea: SUB_PRODUCT_AREAS.SSP,
        },
    },
    {
        path: 'admin/buy-now-redirect',
        name: 'BuyNowRedirect',
        component: () => import('@/free/buy-now/buy-now.vue'),
        meta: {
            title: 'Buy Now Free',
            productArea: PRODUCT_AREAS.GROWTH,
            subProductArea: SUB_PRODUCT_AREAS.SSP,
        },
    },
    {
        path: 'admin/upgrade-now',
        name: 'UpgradeNow',
        component: () => import('@/free/upgrade-now/upgrade-now.vue'),
        meta: {
            title: 'Upgrade Plan',
            productArea: PRODUCT_AREAS.GROWTH,
            subProductArea: SUB_PRODUCT_AREAS.SSP,
        },
    },
    {
        path: 'admin/upgrade-now/preview',
        name: 'UpgradePreview',
        component: () => import('@/free/upgrade-now/upgrade-preview/upgrade-preview.vue'),
        meta: {
            title: 'Upgrade Plan Preview',
            productArea: PRODUCT_AREAS.GROWTH,
            subProductArea: SUB_PRODUCT_AREAS.SSP,
        },
    },
    {
        path: 'admin',
        name: 'Admin',
        component: () => import('@/admin/admin.vue'),
        meta: {
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
            hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
        },
        children: [
            {
                path: 'settings',
                name: 'AdminSubscriptionSettings',
                alias: [''],
                meta: {
                    title: 'Subscription settings',
                    productArea: PRODUCT_AREAS.ADMIN,
                    subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Subscription settings'),
                            params: {},
                            icon: { type: 'settings' },
                            keywords: ['admin'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
                    },
                    hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
                },
            },
            {
                path: 'ai-access',
                name: 'AdminAIAccess',
                meta: {
                    title: 'AI access',
                    productArea: PRODUCT_AREAS.ADMIN,
                    subProductArea: SUB_PRODUCT_AREAS.AI_ACCESS,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.AI access'),
                            params: {},
                            icon: { type: 'settings' },
                            keywords: ['admin', 'ai', 'settings'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
                    },
                    hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
                },
            },
            {
                path: 'applications',
                name: 'AdminApplicationList',
                meta: {
                    title: 'Applications',
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Applications'),
                            params: {},
                            icon: { type: 'settings' },
                            keywords: ['settings', 'admin'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
                    },
                    productArea: PRODUCT_AREAS.ADMIN,
                    subProductArea: SUB_PRODUCT_AREAS.APPLICATION_SETTINGS,
                    hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
                },
            },
            {
                path: 'essential-contacts',
                name: 'AdminEssentialContacts',
                meta: {
                    title: 'Essential Contacts',
                    productArea: PRODUCT_AREAS.ADMIN,
                    subProductArea: SUB_PRODUCT_AREAS.ESSENTIAL_CONTACTS,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Essential contacts'),
                            params: {},
                            icon: { type: 'settings' },
                            keywords: ['settings', 'admin'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasSubEssentialContactsRedirect(),
                },
            },
            {
                path: 'extension',
                name: 'AdminExtensionSettings',
                meta: {
                    title: 'Extension Settings',
                    productArea: PRODUCT_AREAS.ADMIN,
                    subProductArea: SUB_PRODUCT_AREAS.EXTENSION_SETTINGS,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Extension settings'),
                            params: {},
                            icon: { type: 'settings' },
                            keywords: ['settings', 'admin'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasExtensionSettingsRedirect(),
                },
            },
            {
                path: 'licenses',
                name: 'AdminManageLicenses',
                meta: {
                    title: 'Licences',
                    productArea: PRODUCT_AREAS.ADMIN,
                    subProductArea: SUB_PRODUCT_AREAS.LICENSE_UTILIZATION,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Licenses'),
                            params: {},
                            icon: { type: 'settings' },
                            keywords: ['settings', 'admin'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasAdminLicenseUtilizationRedirect(),
                },
            },
            {
                path: 'domains',
                name: 'AdminDomainsSettings',
                meta: {
                    title: 'Domains',
                    productArea: PRODUCT_AREAS.ORCHESTRATE,
                    subProductArea: SUB_PRODUCT_AREAS.EMAIL,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Domains'),
                            params: {},
                            icon: { type: 'settings' },
                            keywords: ['settings', 'admin'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasDomainsRedirect(),
                },
            },
        ],
    },
    {
        path: 'admin/app/:appId',
        name: 'AppAdmin',
        component: () => import('@/admin/app-settings/app-settings.vue'),
        props: true,
        meta: {
            title: 'Application Settings',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Admin'),
            hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.APPLICATION_SETTINGS,
        },
    },
    {
        path: 'admin/data/out',
        name: 'DataOut',
        component: () => import('@/data-out/data-out.vue'),
        meta: {
            hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.INTEGRATIONS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
        },
    },
    {
        path: 'admin/errorlog',
        name: 'ErrorlogAdmin',
        component: () => import('@/admin/error-log/error-log.vue'),
        meta: {
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.ERROR,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Error log'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['settings', 'admin'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
        },
    },
    {
        path: 'admin/exclude-list',
        name: 'Blacklist',
        component: () => import('@/settings/blacklist-staging/blacklist-staging-main.vue'),
        props: { type: 'Manage exclude and include lists' },
        beforeEnter () {
            const redirectForSampleDataRoute = adminRoutesGuard();

            if (redirectForSampleDataRoute) return redirectForSampleDataRoute;
        },
        meta: {
            title: 'Manage exclude and include lists',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.EXCLUDE_INCLUDE_LIST,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Exclude and include lists'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['settings', 'admin'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
        },
    },
    {
        path: 'admin/mappings',
        name: 'DataMappings',
        component: () => import('@/settings/data-mappings/data-mappings.vue'),
        meta: {
            title: 'Metadata',
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.METADATA,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Metadata'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['metadata', 'settings', 'admin'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
        },
    },
    {
        path: 'admin/event-properties',
        name: 'EventProperties',
        component: () => import('@/settings/event-properties/event-properties.vue'),
        meta: {
            title: 'Event properties',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.METADATA,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Event properties'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['settings', 'admin'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
        },
    },
    {
        path: 'admin/staging',
        name: 'Staging',
        component: () => import('@/settings/blacklist-staging/blacklist-staging-main.vue'),
        props: { type: 'Staging' },
        meta: {
            title: 'Staging',
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.STAGING,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Staging'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['settings', 'admin'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
        },
    },
    {
        path: 'adoptv2/admin/partner-settings',
        name: 'AdoptV2Settings',
        component: () => import('@/settings/adopt-settings/adopt-settings.vue'),
        props: true,
        beforeEnter () {
            const subIsAdoptEnabled = store.getters['subscription/isActiveTrainingPartner'];
            const subIsPartnerAnalyticsEnabled = store.getters['subscription/isPartnerAnalytics'];
            const userIsAdoptManager = store.getters['auth/isAdoptManager'];

            const isAdoptEnabledAndAdoptManager = subIsAdoptEnabled && userIsAdoptManager;
            const isPartnerAnalytics = userIsAdoptManager && subIsPartnerAnalyticsEnabled;

            if (!isAdoptEnabledAndAdoptManager && !isPartnerAnalytics) {
                return { name: 'Home' };
            }
        },
        meta: {
            title: 'OEM Settings',
            productArea: PRODUCT_AREAS.ADOPT,
            subProductArea: SUB_PRODUCT_AREAS.PARTNER_SETTINGS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.OEM settings'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['reseller', 'oem', 'admin'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasOEMSettingsRedirect(),
        },
    },
    {
        path: 'admin/users',
        name: 'UsersAdmin',
        component: () => import('@/settings/users/users.vue'),
        props: true,
        meta: {
            title: 'Users',
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.USER_MANAGEMENT,
            navigationQuickLinks: [
                {
                    label: ($t) =>
                        (!activeSubHasPurchasedProductLine('adoptEndUser')
                            ? $t('jumpTo.Users and teams')
                            : $t('jumpTo.Users')),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['settings', 'admin', 'permissions'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => adminRoutesGuard({ isAdoptOnCoreAllowed: true }),
        },
    },
    {
        path: 'admin/users/team/:id',
        name: 'TeamDetails',
        component: () => import('@/settings/teams/team-details.vue'),
        meta: {
            title: 'Team Details',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.USER_MANAGEMENT,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAdminTeamsRedirect(),
        },
    },
    {
        path: 'admin/data-management/:state(setup|estimate)?/:step(choose-destination|connect-destination|select-data)?',
        name: 'DataManagement',
        component: () => import('@/data-management/data-management.vue'),
        meta: {
            title: 'Data Management',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasDataManagementRedirect(),
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.DATA_SYNC,
        },
    },
    {
        path: 'admin/data-management/event-properties',
        name: 'DMEventProperties',
        component: () => import('@/data-management/data-management.vue'),
        meta: {
            title: 'Event properties',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasDataManagementRedirect(),
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.DATA_SYNC,
        },
    },
    {
        path: 'admin/data-management/metadata',
        name: 'DMMetadata',
        component: () => import('@/data-management/data-management.vue'),
        meta: {
            title: 'Metadata',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasDataManagementRedirect(),
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.DATA_SYNC,
        },
    },
    {
        path: 'admin/data-management/events',
        name: 'DMEvents',
        component: () => import('@/data-management/data-management.vue'),
        meta: {
            title: 'Events',
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.DATA_SYNC,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Data Sync events'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['settings', 'admin', 'data management'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasDataManagementRedirect(),
        },
    },
    {
        path: 'admin/data-management/accounts-and-visitors',
        name: 'DMAccountsAndVisitors',
        component: () => import('@/data-management/data-management.vue'),
        meta: {
            title: 'Accounts and visitors',
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.DATA_SYNC,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Data Sync accounts and visitors'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['settings', 'admin', 'data management'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasDataManagementRedirect(),
        },
    },
    {
        path: 'admin/integrations',
        name: 'Integrations',
        component: () => import('@/integrations/integrations.vue'),
        meta: {
            title: 'Integrations',
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.INTEGRATIONS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Integrations catalog'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['settings', 'admin'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasIntegrationsCatalogRedirect(),
        },
    },
    {
        path: 'admin/integrations/catalog/:integrationProvider',
        name: 'IntegrationsProvider',
        component: () => import('@/integrations/integrations.vue'),
        meta: {
            title: 'Integrations',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasIntegrationsRedirect(),
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.INTEGRATIONS,
        },
    },
    {
        path: 'admin/integrations/setup/:integrationProvider',
        name: 'IntegrationsSetup',
        component: () => import('@/integrations/integrations.vue'),
        meta: {
            title: 'Integrations',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasIntegrationsRedirect(),
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.INTEGRATIONS,
        },
    },
    {
        path: 'admin/integrations/setup/:integrationProvider/:integrationName',
        name: 'IntegrationsSetupName',
        component: () => import('@/integrations/integrations.vue'),
        meta: {
            title: 'Integrations',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasIntegrationsRedirect(),
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.INTEGRATIONS,
        },
    },
    {
        path: 'admin/integrations/:type',
        name: 'IntegrationsType',
        component: () => import('@/integrations/integrations.vue'),
        meta: {
            title: 'Integrations',
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.INTEGRATIONS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Webhooks'),
                    params: { type: 'webhooks' },
                    icon: { type: 'settings' },
                    keywords: ['integrations', 'settings'],
                    hasPermissionsForSubRoute: () => hasFeatureFlag('enableWebHooks'),
                },
                {
                    label: ($t) => $t('jumpTo.Integration keys'),
                    params: { type: 'integration-keys' },
                    icon: { type: 'settings' },
                    keywords: ['integrations', 'settings'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasIntegrationsRedirect(),
        },
    },
    {
        path: 'admin/oemSettings',
        name: 'OemSettings',
        component: () => import('@/admin/oem-settings/oem-settings.vue'),
        meta: {
            title: 'Applications',
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Applications'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['settings', 'admin'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasOEMSettingsApplicationRedirect(),
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: 'user-profile',
        name: 'UserProfile',
        component: () => import('@/settings/user-profile/user-profile.vue'),
        props: true,
        meta: {
            title: 'User profile',
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.USER_MANAGEMENT,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.User profile'),
                    params: {},
                    icon: { type: 'settings' },
                    keywords: ['settings'],
                },
            ],
        },
    },
    {
        path: 'roadmaps',
        component: () => import('@/roadmaps/pages/index.vue'),
        children: [
            {
                path: '',
                name: 'RoadmapsList',
                component: () => import('@/roadmaps/components/common/roadmap-list-page/roadmap-list-page.vue'),
                meta: {
                    title: 'Roadmaps',
                    productArea: PRODUCT_AREAS.VOC,
                    subProductArea: SUB_PRODUCT_AREAS.ROADMAPS,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Roadmaps'),
                            params: {},
                            icon: { type: 'plan' },
                            keywords: ['listen'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.ROADMAPS,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasRoadmapsRedirect(),
                },
            },
            {
                path: ':roadmapId',
                name: 'Roadmap',
                component: () => import('@/roadmaps/components/common/roadmap-page/roadmap-page.vue'),
                meta: {
                    title: 'Roadmap',
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasRoadmapsRedirect(),
                    productArea: PRODUCT_AREAS.VOC,
                    subProductArea: SUB_PRODUCT_AREAS.ROADMAPS,
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.ROADMAPS,
                    },
                },
            },
        ],
    },
    {
        path: 'orchestrate',
        name: 'Orchestrate',
        component: () => import('@/orchestrate/journeys/pages/landing-page/landing-page.vue'),
        meta: {
            title: 'Orchestrate',
            productArea: PRODUCT_AREAS.ORCHESTRATE,
            subProductArea: SUB_PRODUCT_AREAS.ORCHESTRATE,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasJourneysRedirect(),
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ORCHESTRATE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.JOURNEYS,
            },
        },
        beforeEnter (to, from, next) {
            const showAvailableUpgrades =
                !store.getters['subscription/getFeatureFlag']('hideUpsellNav') &&
                !store.getters['productLines/hasProductLinePurchased']('adoptEndUser') &&
                !store.getters['subscription/isTrainingSubscription'];

            const hasOrchestrate = store.getters['productLines/hasProductLinePurchased']('orchestrate');

            if (showAvailableUpgrades && !hasOrchestrate) {
                next('/orchestrate');
            } else if (!showAvailableUpgrades && !hasOrchestrate) {
                next('/404');
            } else {
                next();
            }
        },
    },
    {
        path: 'buy-orchestrate',
        name: 'BuyOrchestrate',
        component: () => import('@/orchestrate/journeys/pages/orchestrate-upsell-page/orchestrate-upsell-page.vue'),
        meta: {
            title: 'Buy Orchestrate',
            productArea: PRODUCT_AREAS.ORCHESTRATE,
            subProductArea: SUB_PRODUCT_AREAS.ORCHESTRATE,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ORCHESTRATE_UPSELL,
            },
        },
    },
    {
        path: 'journeys/403',
        name: 'journeyForbidden',
        component: () => import('@/orchestrate/journeys/pages/403/403.vue'),
    },
    {
        path: 'journeys/:id',
        name: 'JourneyDetails',
        props: true,
        component: () => import('@/orchestrate/journeys/pages/journey-details/journey-details.vue'),
        meta: {
            title: 'Journey',
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasJourneysRedirect(),
            productArea: PRODUCT_AREAS.ORCHESTRATE,
            subProductArea: SUB_PRODUCT_AREAS.JOURNEYS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ORCHESTRATE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.JOURNEYS,
            },
        },
    },
    {
        path: 'journeys/:id/messages/:messageId',
        name: 'MessageResults',
        props: true,
        component: () => import('@/orchestrate/journeys/pages/message-results/message-results.vue'),
        meta: {
            productArea: PRODUCT_AREAS.ORCHESTRATE,
            subProductArea: SUB_PRODUCT_AREAS.JOURNEYS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ORCHESTRATE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.JOURNEYS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasJourneysRedirect(),
        },
    },
    {
        path: 'journeys',
        name: 'Journeys',
        component: () => import('@/orchestrate/journeys/pages/landing-page/landing-page.vue'),
        meta: {
            productArea: PRODUCT_AREAS.ORCHESTRATE,
            subProductArea: SUB_PRODUCT_AREAS.JOURNEYS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ORCHESTRATE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.JOURNEYS,
            },
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Journeys'),
                    params: {},
                    icon: { type: 'orchestrate' },
                    keywords: ['orchestrate'],
                },
            ],
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasJourneysRedirect(),
        },
    },
    {
        path: 'spaces',
        name: 'SpacesList',
        component: () => import('@/spaces/index.vue'),
        meta: {
            title: t('spaces.Spaces'),
            prefixDomainTitle: false,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SPACES,
            },
            navigationQuickLinks: [
                {
                    label: ($t) => $t('spaces.Spaces'),
                    params: {},
                    icon: { type: 'layout' },
                    keywords: ['spaces'],
                },
            ],
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSpacesRedirect(),
        },
    },
    {
        path: 'spaces/:id',
        name: 'Space',
        component: () => import('@/spaces/pages/space/space.vue'),
        meta: {
            title: t('spaces.Space'),
            prefixDomainTitle: false,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SPACES,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSpacesRedirect(),
        },
    },
    {
        path: 'upgrade-required/:featureKey',
        name: 'UpgradeRequired',
        props: true,
        component: () => import('@/free/upgrade-required/upgrade-required.vue'),
        meta: {
            productArea: PRODUCT_AREAS.GROWTH,
            subProductArea: SUB_PRODUCT_AREAS.UPSELL,
        },
    },
    {
        path: 'admin/product-areas',
        name: 'ProductAreasAdmin',
        component: () => import('@/settings/product-areas/product-areas.vue'),
        meta: {
            title: 'Product Areas',
            productArea: PRODUCT_AREAS.ADMIN,
            subProductArea: SUB_PRODUCT_AREAS.PRODUCT_AREAS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            hasPermissionsDeniedRedirect: () => adminRoutesGuard(),
        },
    },
    {
        path: 'search/:kind',
        name: 'SearchResults',
        component: () => import('@/visitor/search-results.vue'),
        props: true,
        meta: {
            title: 'Search Results',
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.GENERAL,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
            },
        },
    },
    {
        path: 'visitors',
        name: 'Visitors',
        component: () => import('@/people/kind-list/kind-list.vue'),
        meta: {
            title: 'Visitors',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.VISITORS,
            },
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.VISITOR_LIST_DETAILS,
            agentModeCountableType: 'visitor',
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Visitors'),
                    params: {},
                    icon: { type: 'users' },
                },
            ],
        },
        children: [
            {
                path: 'reports',
                name: 'VisitorList',
                component: () => import('@/people/kind-list/kind-list.vue'),
                meta: {
                    title: 'Visitor reports',
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.VISITORS,
                    },
                    productArea: PRODUCT_AREAS.ANALYTICS,
                    subProductArea: SUB_PRODUCT_AREAS.VISITOR_LIST_DETAILS,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasVisitorReportsRedirect(),
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Visitor reports'),
                            params: {},
                            icon: { type: 'users' },
                        },
                    ],
                },
            },
        ],
    },
    {
        path: 'visitor/:visitorId',
        name: 'VisitorDetails',
        component: () => import('@/visitor/visitor-details/visitor-details.vue'),
        meta: {
            title: 'Visitor details',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.VISITOR_LIST_DETAILS,
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Visitors'),
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.VISITORS,
            },
            agentModeCountableType: AGENT_MODE_COUNTABLE_BY_ROUTE.VisitorDetails,
        },
    },
    {
        path: 'accounts',
        name: 'Accounts',
        component: () => import('@/people/kind-list/kind-list.vue'),
        meta: {
            title: 'Accounts',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.ACCOUNTS,
            },
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.ACCOUNT_LIST_DETAILS,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAccountsRedirect(),
            agentModeCountableType: 'account',
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Accounts'),
                    params: {},
                    icon: { type: 'users' },
                },
            ],
        },
        children: [
            {
                path: 'reports',
                name: 'AccountList',
                component: () => import('@/people/kind-list/kind-list.vue'),
                meta: {
                    title: 'Account reports',
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.ACCOUNTS,
                    },
                    productArea: PRODUCT_AREAS.ANALYTICS,
                    subProductArea: SUB_PRODUCT_AREAS.ACCOUNT_LIST_DETAILS,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasAccountsRedirect(),
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Account reports'),
                            params: {},
                            icon: { type: 'users' },
                        },
                    ],
                },
            },
            {
                path: 'parentAccountReports',
                name: 'ParentAccountList',
                component: () => import('@/people/kind-list/kind-list.vue'),
                meta: {
                    title: 'Parent Account reports',
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.ACCOUNTS,
                    },
                    productArea: PRODUCT_AREAS.ANALYTICS,
                    subProductArea: SUB_PRODUCT_AREAS.ACCOUNT_LIST_DETAILS,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Parent Account reports'),
                            params: {},
                            icon: { type: 'users' },
                        },
                    ],
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasParentAccountsRedirect(),
                },
            },
        ],
    },
    {
        path: 'visitors/reports/:reportId',
        name: 'VisitorListReport',
        component: () => import('@/reports/components/kind-report/kind-report.vue'),
        props: true,
        meta: {
            title: 'Visitor report',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.VISITORS,
            },
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.VISITOR_LIST_REPORT,
            switchSubscriptionToRoute: switchSubscriptionToRouteName('VisitorList'),
            agentModeCountableType: 'visitor',
        },
    },
    {
        path: 'accounts/reports/:reportId',
        name: 'AccountListReport',
        component: () => import('@/reports/components/kind-report/kind-report.vue'),
        props: true,
        meta: {
            title: 'Account report',
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAccountsRedirect(),
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.ACCOUNTS,
            },
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.ACCOUNT_LIST_REPORT,
            switchSubscriptionToRoute: switchSubscriptionToRouteName('AccountList'),
            agentModeCountableType: 'account',
        },
    },
    {
        path: 'accounts/parentAccountReports/:reportId',
        name: 'ParentAccountListReport',
        component: () => import('@/reports/components/kind-report/kind-report.vue'),
        props: true,
        meta: {
            title: 'Parent account report',
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasParentAccountsRedirect(),
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.ACCOUNTS,
            },
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.PARENT_ACCOUNT_LIST_REPORT,
            switchSubscriptionToRoute: switchSubscriptionToRouteName('ParentAccountList'),
            agentModeCountableType: 'parentAccount',
        },
    },
    {
        path: 'visitorlist',
        children: [
            {
                path: '',
                redirect: { name: 'VisitorList' },
            },
            {
                path: ':reportId',
                redirect: (to) => {
                    return `/visitors/reports/${to.params.reportId}`;
                },
            },
            {
                path: 'tmp',
                redirect: (encodedReport) => {
                    return `/kind-report/adhoc/${encodedReport.hash.slice(1)}`;
                },
            },
        ],
    },
    {
        path: 'accountlist',
        children: [
            {
                path: '',
                redirect: { name: 'AccountList' },
            },
            {
                path: ':reportId',
                redirect: (to) => {
                    return `/accounts/reports/${to.params.reportId}`;
                },
            },
            {
                path: 'tmp',
                redirect: (encodedReport) => {
                    return `/kind-report/adhoc/${encodedReport.hash.slice(1)}`;
                },
            },
        ],
    },
    {
        path: 'parentaccountlist',
        children: [
            {
                path: '',
                redirect: { name: 'ParentAccountList' },
            },
            {
                path: ':reportId',
                redirect: (to) => {
                    return `/accounts/parentAccountReports/${to.params.reportId}`;
                },
            },
            {
                path: 'tmp',
                redirect: (encodedReport) => {
                    return `/kind-report/adhoc/${encodedReport.hash.slice(1)}`;
                },
            },
        ],
    },
    {
        path: 'kind-report/adhoc/:encodedReport',
        name: 'AdhocKindReport',
        component: () => import('@/reports/components/kind-report/kind-report.vue'),
        props: true,
        meta: {
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.ADHOC_REPORTS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
            },
        },
    },
    {
        path: 'account/:accountId',
        name: 'AccountDetails',
        component: () => import('@/accounts/account-details/account-details.vue'),
        meta: {
            title: 'Account Details',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.ACCOUNT_DETAILS,
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Accounts'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAccountsRedirect(),
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.ACCOUNTS,
            },
            agentModeCountableType: AGENT_MODE_COUNTABLE_BY_ROUTE.AccountDetails,
        },
    },
    {
        path: 'parentAccount/:accountId',
        name: 'ParentAccountDetails',
        component: () => import('@/accounts/account-details/account-details.vue'),
        meta: {
            title: 'Parent Account Details',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.PARENT_ACCOUNT_DETAILS,
            switchSubscriptionToRoute: switchSubscriptionToRouteName('ParentAccounts'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasParentAccountsRedirect(),
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PEOPLE,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.ACCOUNTS,
            },
        },
    },
    {
        path: 'paths',
        name: 'PathList',
        component: () => import('@/reports/path/components/path-list.vue'),
        props: true,
        meta: {
            title: 'Paths',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.PATHS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Paths'),
                    params: {},
                    icon: { type: 'bar-chart-big' },
                    keywords: ['analytics', 'reports'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasPathsAccessRedirect(),
        },
    },
    {
        path: 'path/:reportId?',
        name: 'PathDetails',
        component: () => import('@/reports/path/components/path-details/path-details.vue'),
        props: true,
        meta: {
            title: 'Paths',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SavedReports'),
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.PATHS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasPathsAccessRedirect(),
            hideFooter: true,
        },
    },
    {
        path: 'funnels',
        name: 'FunnelList',
        component: () => import('@/reports/funnel/components/funnel-list.vue'),
        props: true,
        meta: {
            title: 'Funnels',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.FUNNELS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Funnels'),
                    params: {},
                    icon: { type: 'bar-chart-big' },
                    keywords: ['analytics', 'reports'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasFunnelsAccessRedirect(),
        },
    },
    {
        path: 'funnel/:reportId?',
        name: 'FunnelDetails',
        component: () => import('@/reports/funnel/components/funnel-details/funnel-details.vue'),
        props: true,
        meta: {
            title: 'Funnels',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SavedReports'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasFunnelsAccessRedirect(),
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.FUNNELS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
        },
    },
    {
        path: 'retention-reports',
        name: 'RetentionList',
        component: () => import('@/retention/retention-list.vue'),
        props: true,
        meta: {
            title: 'Retention Reports',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.RETENTION,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Retention reports'),
                    params: {},
                    icon: { type: 'bar-chart-big' },
                    keywords: ['analytics'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasRetentionAccessRedirect(),
        },
    },
    {
        path: 'retention/:reportId?',
        name: 'RetentionDetails',
        component: () => import('@/retention/retention-details.vue'),
        props: true,
        meta: {
            title: 'Retention',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.RETENTION,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasRetentionAccessRedirect(),
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SavedReports'),
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
            hideFooter: true,
        },
    },
    {
        path: 'trackevents',
        name: 'TrackEvents',
        component: () => import('@/track-events/track-events-list.vue'),
        meta: {
            title: 'Track Events',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.TRACK_EVENT_LIST_DETAILS,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasOEMProductAnalyticsRedirect(),
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Track Events'),
                    params: {},
                    icon: { type: 'layout' },
                    keywords: ['product'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.TRACK_EVENTS,
            },
        },
    },
    {
        path: 'trackevents/:trackTypeId',
        name: 'TrackEventsDetails',
        component: () => import('@/track-events/track-type-details/track-event-details.vue'),
        meta: {
            title: 'Track Event Details',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('TrackEvents'),
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.TRACK_EVENT_LIST_DETAILS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.TRACK_EVENTS,
            },
            agentModeCountableType: AGENT_MODE_COUNTABLE_BY_ROUTE.TrackEventsDetails,
        },
    },
    {
        path: 'onboarding',
        name: 'OnboardingHomepage',
        component: () => import('@/onboarding-homepage/components/onboarding-homepage.vue'),
        meta: {
            title: 'Welcome to Pendo.io',
            productArea: PRODUCT_AREAS.GROWTH,
            subProductArea: SUB_PRODUCT_AREAS.ONBOARDING,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.HOME,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasHomeRedirect(),
        },
    },
    {
        path: 'home',
        name: 'Homepage',
        component: () => {
            if (store.getters['auth/hasSegmentFlag']('agentHomepage')) {
                return import('@/homepage/agent-homepage/agent-homepage.vue');
            }

            return import('@/homepage/homepage.vue');
        },
        meta: {
            title: 'Welcome to Pendo.io',
            productArea: PRODUCT_AREAS.GROWTH,
            subProductArea: SUB_PRODUCT_AREAS.HOMEPAGE,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.HOME,
            },
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Home'),
                    params: {},
                    icon: { type: 'home' },
                    keywords: ['homepage'],
                },
            ],
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasHomeRedirect(),
        },
    },
    {
        path: 'pes',
        name: 'ProductEngagementScore',
        component: () => import('@/product-engagement-score/product-engagement-score.vue'),
        meta: {
            title: 'Product Engagement Score',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.PRODUCT_ENGAGEMENT_SCORE,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Product Engagement Score'),
                    params: {},
                    icon: { type: 'bar-chart-big' },
                    keywords: ['analytics'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.PES,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasPESRedirect(),
        },
    },
    {
        path: 'dashboard',
        name: 'DefaultDashboard',
        component: () => import('@/dashboards-v2/dashboards.vue'),
        meta: {
            title: 'Dashboard',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.DASHBOARDS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.DASHBOARDS,
            },
        },
    },
    {
        path: 'dashboards/:id',
        name: 'Dashboard',
        component: () => import('@/dashboards-v2/dashboards.vue'),
        meta: {
            title: 'Dashboard',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('DefaultDashboard'),
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.DASHBOARDS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.DASHBOARDS,
            },
        },
    },
    {
        path: 'dashboards',
        name: 'DashboardsList',
        component: () => import('@/dashboards-v2/dashboards-list.vue'),
        meta: {
            title: 'Dashboard Directory',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.DASHBOARDS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.DASHBOARDS,
            },
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Dashboards'),
                    params: {},
                    icon: { type: 'dashboard' },
                },
            ],
        },
    },
    {
        path: 'workflows',
        name: 'WorkflowList',
        component: () => import('@/workflows/components/workflow-list.vue'),
        meta: {
            title: 'Workflows',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.WORKFLOWS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Workflows'),
                    params: {},
                    icon: { type: 'bar-chart-big' },
                    keywords: ['analytics', 'reports'],
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasWorkflowsRedirect(),
        },
    },
    {
        path: 'workflow',
        name: 'CreateWorkflow',
        component: () => import('@/workflows/components/workflow.vue'),
        meta: {
            title: 'Create Workflow and Journeys',
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasWorkflowsRedirect(),
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.WORKFLOWS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
        },
    },
    {
        path: 'workflow/:reportId?',
        component: () => import('@/workflows/components/workflow.vue'),
        props: true,
        meta: {
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.WORKFLOWS,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasWorkflowsRedirect(),
            switchSubscriptionToRoute: switchSubscriptionToRouteName('SavedReports'),
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
            },
        },
        children: [
            {
                path: ':tab?',
                name: 'WorkflowDetails',
                component: () => import('@/workflows/components/workflow.vue'),
                props: true,
                meta: {
                    title: 'Workflow and Journeys',
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasWorkflowsRedirect(),
                    productArea: PRODUCT_AREAS.ANALYTICS,
                    subProductArea: SUB_PRODUCT_AREAS.WORKFLOWS,
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('SavedReports'),
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.ANALYTICS,
                        secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.REPORTS,
                    },
                },
            },
        ],
    },
    {
        path: 'portfolio/:tab?',
        name: 'Portfolio',
        component: () => import('@/portfolio/portfolio.vue'),
        props: true,
        meta: {
            title: 'Portfolio',
            productArea: PRODUCT_AREAS.ANALYTICS,
            subProductArea: SUB_PRODUCT_AREAS.PORTFOLIO,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Portfolio overview'),
                    params: { tab: 'overview' },
                    icon: { type: 'layout' },
                    hasPermissionsForSubRoute: () => !RoutePermissions.hasPortfolioOverviewRedirect(),
                },
                {
                    label: ($t) => $t('jumpTo.Application usage'),
                    params: { tab: 'application-usage' },
                    icon: { type: 'layout' },
                    keywords: ['portfolio', 'applications'],
                    hasPermissionsForSubRoute: () => !RoutePermissions.hasApplicationUsageRedirect(),
                },
                {
                    label: ($t) => $t('jumpTo.Employee license utilization'),
                    params: { tab: 'license-utilization' },
                    icon: { type: 'layout' },
                    keywords: ['portfolio'],
                    hasPermissionsForSubRoute: () => !RoutePermissions.hasLicenseUtilizationRedirect(),
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PRODUCT,
                secondaryOptionName: SECONDARY_LEVEL_NAV_OPTIONS.PORTFOLIO,
            },
            hasPermissionsDeniedRedirect: () =>
                RoutePermissions.hasOEMProductAnalyticsRedirect() ||
                (RoutePermissions.hasPortfolioOverviewRedirect() &&
                    RoutePermissions.hasApplicationUsageRedirect() &&
                    RoutePermissions.hasLicenseUtilizationRedirect()),
        },
    },
    {
        path: 'portfolio-overview',
        name: 'PortfolioOverview',
        redirect: (to) => {
            const redirect = RoutePermissions.hasPortfolioOverviewRedirect();

            return (
                redirect || {
                    name: 'Portfolio',
                    params: { ...to.params, tab: 'overview' },
                    query: to.query,
                }
            );
        },
    },
    {
        path: 'application-usage',
        name: 'ApplicationUsage',
        redirect: (to) => {
            const redirect = RoutePermissions.hasApplicationUsageRedirect();

            return (
                redirect || {
                    name: 'Portfolio',
                    params: { ...to.params, tab: 'application-usage' },
                    query: to.query,
                }
            );
        },
    },
    {
        path: 'license-utilization',
        name: 'LicenseUtilization',
        redirect: (to) => {
            const redirect = RoutePermissions.hasLicenseUtilizationRedirect();

            return (
                redirect || {
                    name: 'Portfolio',
                    params: { ...to.params, tab: 'license-utilization' },
                    query: to.query,
                }
            );
        },
    },
    {
        path: 'net-promoter-system',
        name: 'NPS',
        props: true,
        meta: {
            title: 'NPS',
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.NPS'),
                    params: {},
                    icon: { type: 'heart' },
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.NPS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasNPSRedirect(),
        },
        component: () => import('@/net-promoter-system/nps-list/nps-list.vue'),
    },
    {
        path: 'net-promoter-system/guides/:guideId',
        name: 'NPSGuideDetails',
        props: true,
        component: () => import('@/guides/p2-guide-details/guide-details.vue'),
        meta: {
            title: 'NPS details',
            activeNavOption: {
                topOptionName: () => {
                    return !RoutePermissions.hasSurveysRedirect()
                        ? TOP_LEVEL_NAV_OPTIONS.SURVEYS
                        : TOP_LEVEL_NAV_OPTIONS.NPS;
                },
            },
        },
    },
    {
        path: 'sentiment',
        name: 'SurveysUpsell',
        props: true,
        meta: {
            title: 'Sentiment',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SURVEYS_UPSELL,
            },
        },
        component: () => import('@/surveys/pages/surveys-upsell-page/surveys-upsell-page.vue'),
    },
    {
        path: 'surveys',
        component: () => import('@/surveys/pages/index.vue'),
        children: [
            {
                path: '',
                name: 'SurveysList',
                component: () => import('@/surveys/pages/survey-list/survey-list.vue'),
                meta: {
                    title: 'Surveys list',
                    productArea: PRODUCT_AREAS.SURVEYS,
                    prefixDomainTitle: false,
                    navigationQuickLinks: [
                        {
                            label: ($t) => $t('jumpTo.Sentiment'),
                            params: {},
                            icon: { type: 'surveys' },
                            keywords: ['NPS'],
                        },
                    ],
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SURVEYS,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasSurveysRedirect(),
                },
            },
            {
                path: 'create',
                name: 'SurveyCreate',
                meta: {
                    title: 'Create survey',
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasSurveysRedirect(),
                    productArea: PRODUCT_AREAS.SURVEYS,
                    prefixDomainTitle: false,
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SURVEYS,
                    },
                },
                component: () => import('@/surveys/pages/create-survey-wizard/create-survey-wizard.vue'),
            },
            {
                path: ':id',
                name: 'SurveyDetails',
                component: () => import('@/surveys/pages/survey-details/survey-details.vue'),
                meta: {
                    title: 'Survey details',
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasSurveysRedirect(),
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('SurveysList'),
                    productArea: PRODUCT_AREAS.SURVEYS,
                    prefixDomainTitle: false,
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SURVEYS,
                    },
                },
            },
        ],
    },
    {
        path: 'surveys/reports',
        component: () => import('@/surveys/pages/index.vue'),
        children: [
            {
                path: '',
                name: 'SurveyReportsList',
                component: () => import('@/surveys/pages/survey-reports-list/survey-reports-list.vue'),
                meta: {
                    title: 'Survey Reports',
                    productArea: PRODUCT_AREAS.SURVEYS,
                    prefixDomainTitle: false,
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SURVEYS,
                    },
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasSurveysRedirect(),
                },
            },
            {
                path: ':id',
                name: 'SurveyReportDetails',
                component: () => import('@/surveys/pages/survey-report-details/survey-report-details.vue'),
                meta: {
                    title: 'Survey Report',
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasSurveysRedirect(),
                    switchSubscriptionToRoute: switchSubscriptionToRouteName('SurveyReportsList'),
                    productArea: PRODUCT_AREAS.SURVEYS,
                    prefixDomainTitle: false,
                    activeNavOption: {
                        topOptionName: TOP_LEVEL_NAV_OPTIONS.SURVEYS,
                    },
                },
            },
        ],
    },
    {
        path: 'agent-mode',
        name: 'AgentMode',
        component: () => import('@/ask-pendo/ask-pendo-landing.vue'),
        meta: {
            title: 'Agent mode',
            prefixDomainTitle: false,
            productArea: PRODUCT_AREAS.ASK_PENDO,
            hideFooter: true,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAgentModeRedirect(),
        },
    },
    {
        path: 'agent-mode/:id',
        name: 'AgentModeConversation',
        component: () => import('@/ask-pendo/ask-pendo-conversation.vue'),
        props: true,
        meta: {
            title: 'Agent mode conversation',
            prefixDomainTitle: false,
            productArea: PRODUCT_AREAS.ASK_PENDO,
            hideFooter: true,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAgentModeRedirect(),
        },
    },
    {
        path: 'leo',
        name: 'Leo',
        component: () => import('@/ask-pendo/ask-pendo-landing.vue'),
        meta: {
            title: 'Leo',
            prefixDomainTitle: false,
            productArea: PRODUCT_AREAS.ASK_PENDO,
            hideFooter: true,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAgentModeRedirect(),
        },
    },
    {
        path: 'leo/:id',
        name: 'LeoConversation',
        component: () => import('@/ask-pendo/ask-pendo-conversation.vue'),
        props: true,
        meta: {
            title: 'Leo conversation',
            prefixDomainTitle: false,
            productArea: PRODUCT_AREAS.ASK_PENDO,
            hideFooter: true,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasAgentModeRedirect(),
        },
    },
    {
        path: 'leo/listen',
        name: 'ListenAgent',
        component: () => import('@/ask-pendo/ask-pendo-landing.vue'),
        meta: {
            title: 'Listen Agent',
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenAgentRedirect(),
            prefixDomainTitle: false,
        },
    },
    {
        path: 'leo/listen/:id',
        name: 'ListenAgentConversation',
        component: () => import('@/ask-pendo/ask-pendo-conversation.vue'),
        hasPermissionsDeniedRedirect: () => RoutePermissions.hasListenAgentRedirect(),
        props: true,
        meta: {
            title: 'Listen Agent Conversation',
            prefixDomainTitle: false,
            hideFooter: true,
        },
    },
];

const globalRoutes = [
    {
        path: '/no-access',
        name: 'NoAccess',
        component: () => import('@/errors/no-access.vue'),
        meta: {
            title: 'Not Found',
        },
    },
    {
        path: '/oauth/authorize',
        name: 'OAuthAuthorize',
        component: () => import('@/oauth/authorize/authorize.vue'),
        props (route) {
            return {
                query: route.query,
            };
        },
        meta: {
            // This is a workaround for preventing this page from flashing an
            // error state for logged out users before the login redirect kicks
            // in and should be removed as part of the fix for APP-137597.
            hasPermissionsDeniedRedirect: () => false,
        },
    },
    {
        path: '/teams-auth-callback',
        name: 'TeamsAuthCallback',
        component: () => import('@/integrations/teams-auth-callback.vue'),
        meta: {
            title: 'Authenticating with Teams',
        },
    },
    {
        path: '/invalid-invitation-data',
        alias: ['/invalid-invitation-key', '/invitation-expired'],
        name: 'InvitationError',
        component: InvitationError,
        meta: {
            title: 'Invitation Error',
            productArea: PRODUCT_AREAS.PUBLIC,
            subProductArea: SUB_PRODUCT_AREAS.USER_MANAGEMENT,
            skipLoginRedirect: true,
        },
    },
    {
        path: '/trial-expired',
        name: 'TrialExpired',
        component: () => import('@/trial-expired/trial-expired.vue'),
        meta: {
            skipLoginRedirect: true,
        },
    },
    {
        path: '/deep-links/:data',
        name: 'DeepLinks',
        component: () => import('@/vue-router/deep-links/deep-links.vue'),
        meta: {
            skipLoginRedirect: true,
        },
    },
    {
        path: '/post-register-steps',
        name: 'PostRegisterSteps',
        meta: {
            productArea: PRODUCT_AREAS.GROWTH,
            subProductArea: SUB_PRODUCT_AREAS.ONBOARDING,
        },
        component: () => import('@/registration/post-register-steps/post-register-steps.vue'),
    },
    {
        path: '/register',
        name: 'Register',
        component: () => import('@/registration/registration-views.vue'),
        meta: {
            title: 'Register',
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.USER_REGISTRATION,
            skipLoginRedirect: true,
            component: 'Register',
        },
    },
    {
        path: '/login',
        name: 'Login',
        meta: {
            component: 'Login',
            title: 'Login',
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.GENERAL,
            skipLoginRedirect: true,
        },
        component: () => import('@/registration/registration-views.vue'),
        beforeEnter: beforeEnterGuardRedirectByAfter,
    },
    {
        path: '/forgot',
        name: 'ForgotPasswordForm',
        component: () => import('@/registration/registration-views.vue'),
        meta: {
            component: 'ForgotPasswordForm',
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.USER_MANAGEMENT,
            skipLoginRedirect: true,
        },
        props: true,
    },
    {
        path: '/inactive',
        name: 'Inactive',
        component: () => import('@/registration/registration-views.vue'),
        meta: {
            component: 'Inactive',
            skipLoginRedirect: true,
        },
    },
    {
        path: '/invitation/confirm/:email/:inviteKey',
        name: 'Confirmation',
        component: () => import('@/registration/registration-views.vue'),
        meta: {
            component: 'Confirmation',
            title: 'Confirmation',
            skipLoginRedirect: true,
        },
    },
    {
        path: '/setup',
        name: 'Setup',
        component: () => import('@/setup/setup.vue'),
        meta: {
            title: 'Setup',
            productArea: PRODUCT_AREAS.PUBLIC,
            subProductArea: SUB_PRODUCT_AREAS.SETUP,
        },
    },
    {
        path: '/org-admin/:tab?',
        name: 'OrgAdmin',
        component: () => import('@/org/org-admin.vue'),
        meta: {
            enableSubId: false,
            title: 'Organization Admin',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.ORGANZATION_SETTINGS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Plan details'),
                    params: { tab: 'planDetails' },
                    icon: { type: 'settings' },
                    keywords: ['settings', 'organization', 'org settings', 'admin'],
                    hasPermissionsForSubRoute: () => hasOrgTabsPermissions('planDetails'),
                },
                {
                    label: ($t) => $t('jumpTo.MAU'),
                    params: { tab: 'mau' },
                    icon: { type: 'settings' },
                    keywords: ['settings', 'organization', 'org settings', 'admin'],
                    hasPermissionsForSubRoute: () => hasOrgTabsPermissions('mau'),
                },
                {
                    label: ($t) => $t('jumpTo.CNAME'),
                    params: { tab: 'cname' },
                    icon: { type: 'settings' },
                    keywords: ['settings', 'organization', 'org settings', 'admin'],
                    hasPermissionsForSubRoute: () => hasOrgTabsPermissions('cname'),
                },
                {
                    label: ($t) => $t('jumpTo.Domain verification'),
                    params: { tab: 'domainVerification' },
                    icon: { type: 'settings' },
                    keywords: ['settings', 'organization', 'org settings', 'admin'],
                    hasPermissionsForSubRoute: () => hasOrgTabsPermissions('domainVerification'),
                },
                {
                    label: ($t) => $t('jumpTo.SSO'),
                    params: { tab: 'sso' },
                    icon: { type: 'settings' },
                    keywords: ['settings', 'organization', 'single sign on', 'org settings', 'admin'],
                    hasPermissionsForSubRoute: () => hasOrgTabsPermissions('sso'),
                },
                {
                    label: ($t) => $t('jumpTo.Organization users'),
                    params: { tab: 'users' },
                    icon: { type: 'settings' },
                    keywords: ['settings', 'organization', 'org settings', 'admin'],
                    hasPermissionsForSubRoute: () => hasOrgTabsPermissions('users'),
                },
                {
                    label: ($t) => $t('jumpTo.Organization library'),
                    params: { tab: 'orgLibrary' },
                    icon: { type: 'settings' },
                    keywords: ['settings', 'org settings', 'admin'],
                    hasPermissionsForSubRoute: () => hasOrgTabsPermissions('orgLibrary'),
                },
                {
                    label: ($t) => $t('jumpTo.Organization essential contacts'),
                    params: { tab: 'essential-contacts' },
                    icon: { type: 'settings' },
                    hasPermissionsForSubRoute: () => hasOrgTabsPermissions('essentialContacts'),
                },
            ],
            hasPermissionsDeniedRedirect: () =>
                RoutePermissions.hasBasicOrgAdminRedirect({
                    isAEU: store.getters['subscription/isAdoptEndUser'],
                }),
        },
        props: true,
    },
    {
        path: '/org-admin/cname/:subId',
        name: 'OrgAdminSubApps',
        component: () => import('@/org/org-admin-sub-apps.vue'),
        meta: {
            title: 'Organization Admin Subscription',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.ORGANZATION_SETTINGS,
        },
        props: true,
    },
    {
        path: '/org-admin/cname/:subId/config',
        name: 'OrgAdminCnameConfig',
        component: () => import('@/org/org-admin-cname-config.vue'),
        meta: {
            title: 'Organization Admin CNAME Configuration',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.ORGANZATION_SETTINGS,
        },
        props: true,
    },
    {
        path: '/register/complete/:email/:key',
        name: 'CompleteUser',
        meta: {
            component: 'CompleteUser',
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.USER_REGISTRATION,
            skipLoginRedirect: true,
        },
        component: () => import('@/registration/registration-views.vue'),
        props: true,
    },
    {
        path: '/register/resetpassword/:email/:key',
        name: 'ResetPassword',
        component: () => import('@/registration/registration-views.vue'),
        meta: {
            component: 'ResetPassword',
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.USER_MANAGEMENT,
            skipLoginRedirect: true,
        },
        props: true,
    },
    {
        path: '/resetpassword/:email/expiredlink',
        name: 'ExpiredLink',
        component: () => import('@/registration/registration-views.vue'),
        meta: {
            component: 'ExpiredLink',
            skipLoginRedirect: true,
        },
    },
    {
        path: '/super/subs',
        redirect: '/pendozer/subs',
    },
    {
        path: '/pendozer/subs',
        name: 'Subscriptions',
        component: () => import('@/super/super.vue'),
        meta: {
            title: 'Subscriptions',
            productArea: PRODUCT_AREAS.INTERNAL,
            subProductArea: SUB_PRODUCT_AREAS.GENERAL,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Super sub list'),
                    params: {},
                    icon: { type: 'settings' },
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperSubsListRedirect(),
        },
    },
    {
        path: '/ops/jobs',
        name: 'OpsJobs',
        component: () => import('@/ops/jobs/jobs.vue'),
        meta: {
            title: 'Jobs',
            productArea: PRODUCT_AREAS.INTERNAL,
            subProductArea: SUB_PRODUCT_AREAS.GENERAL,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Jobs'),
                    params: {},
                    icon: { type: 'settings' },
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasJobsAndAggsRedirect(),
        },
    },
    {
        path: '/super/subs/:id',
        name: 'SubscriptionDetails',
        props: true,
        component: () => import('@/super/subscription-detail-view/subscription-detail-view.vue'),
        meta: {
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            title: 'Subscription Details',
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperSubsListRedirect(),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
        children: [
            {
                path: 'settings',
                name: 'SuperSubscriptionSettings',
                alias: [''],
                meta: {
                    title: 'Subscription Details - Settings',
                    productArea: PRODUCT_AREAS.PLATFORM,
                    subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperSubsListRedirect(),
                },
            },
            {
                path: 'ai-access',
                name: 'SuperSubscriptionAIAccess',
                meta: {
                    title: 'Subscription Details - AI Access',
                    productArea: PRODUCT_AREAS.PLATFORM,
                    subProductArea: SUB_PRODUCT_AREAS.AI_ACCESS,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperSubsListRedirect(),
                },
            },
            {
                path: 'extension',
                name: 'SuperSubscriptionExtension',
                meta: {
                    title: 'Subscription Details - Extension',
                    productArea: PRODUCT_AREAS.PLATFORM,
                    subProductArea: SUB_PRODUCT_AREAS.EXTENSION_SETTINGS,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperSubsListRedirect(),
                },
            },
            {
                path: 'applications',
                name: 'SuperSubscriptionApplications',
                meta: {
                    title: 'Subscription Details - Applications',
                    productArea: PRODUCT_AREAS.PLATFORM,
                    subProductArea: SUB_PRODUCT_AREAS.APPLICATION_SETTINGS,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperSubsListRedirect(),
                },
            },
            {
                path: 'users',
                name: 'SuperSubscriptionUsers',
                meta: {
                    title: 'Subscription Details - Users',
                    productArea: PRODUCT_AREAS.PLATFORM,
                    subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
                    hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperSubsListRedirect(),
                },
            },
        ],
    },
    {
        path: '/orgs/:orgId',
        name: 'OrganizationDetails',
        component: () => import('@/super/organization-detail-view/organization-detail-view.vue'),
        props: true,
        meta: {
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.SETTINGS,
            },
            title: 'Organization Details',
            productArea: PRODUCT_AREAS.ORGANIZATION,
            subProductArea: SUB_PRODUCT_AREAS.ORGANIZATION_DETAILS,
        },
    },
    {
        path: '/super/subs/:id/feedback-linking',
        name: 'FeedbackLinking',
        props: true,
        component: () => import('@/super/feedback-linking/feedback-linking.vue'),
        meta: {
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            title: 'Feedback Linking',
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: '/super/subs/:id/feedback-auto-setup',
        name: 'FeedbackAutoSetup',
        props: true,
        component: () => import('@/super/feedback-linking/feedback-auto-setup.vue'),
        meta: {
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            title: 'Feedback Setup',
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: '/super/subs/:id/adopt-product-settings/:productTypeKebab',
        name: 'AdoptProductSettings',
        props: true,
        component: () => import('@/super/adopt-product-settings/adopt-product-settings.vue'),
        meta: {
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            title: 'Adopt Product Settings',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Admin'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: '/super/subs/:id/analytics-settings',
        name: 'AnalyticsSettings',
        props: true,
        component: () => import('@/settings/subscription-settings/analytics-settings.vue'),
        meta: {
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            title: 'Analytics Settings',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Admin'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: '/super/subs/:id/one-ui-engineering-use',
        name: 'OneUIEngineeringUse',
        props: true,
        component: () => import('@/super/one-ui-engineering-use/one-ui-engineering-use.vue'),
        meta: {
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            title: 'One UI Engineering Use',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Admin'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: '/super/subs/:id/session-replay-settings',
        name: 'SessionReplaySettings',
        props: true,
        component: () => import('@/super/session-replay-settings/session-replay-settings.vue'),
        meta: {
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            title: 'Session Replay Settings',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Admin'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.SESSION_REPLAY,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: '/super/subs/:id/guides-product-line-settings',
        name: 'GuidesProductLineSettings',
        props: true,
        component: () => import('@/settings/subscription-settings/guides-product-line-settings.vue'),
        meta: {
            enableSubId: false,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            title: 'Guides Product Line Settings',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Admin'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: '/super/subs/:id/listen-product-line-settings',
        name: 'VocProductLineSettings',
        props: true,
        component: () => import('@/settings/subscription-settings/voc-product-line-settings'),
        meta: {
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            title: 'Discover Product Line Settings',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Admin'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: '/super/subs/:id/orchestrate-product-line-settings',
        name: 'OrchestrateProductLineSettings',
        props: true,
        component: () => import('@/settings/subscription-settings/orchestrate-product-line-settings'),
        meta: {
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            title: 'Orchestrate Product Line Settings',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Admin'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: '/super/subs/:id/agent-analytics-product-line-settings',
        name: 'AgentAnalyticsSettings',
        props: true,
        component: () => import('@/settings/subscription-settings/agent-analytics-settings.vue'),
        meta: {
            enableSubId: false,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            title: 'Agent Analytics Product Line Settings',
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Admin'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: '/super/subs/:id/nps-product-line-settings',
        name: 'NpsProductLineSettings',
        props: true,
        component: () => import('@/settings/subscription-settings/nps-product-line-settings'),
        meta: {
            title: 'NPS Product Line Settings',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Admin'),
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
        },
    },
    {
        path: '/super/subs/:id/core-settings',
        name: 'CoreSettings',
        component: () => import('@/settings/subscription-settings/core-settings.vue'),
        meta: {
            title: 'Core Settings',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            switchSubscriptionToRoute: switchSubscriptionToRouteName('Admin'),
            productArea: PRODUCT_AREAS.PLATFORM,
            subProductArea: SUB_PRODUCT_AREAS.SETTINGS,
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
        },
    },
    {
        path: '/ops/aggregations',
        name: 'Aggregations',
        component: () => import('@/ops/agg-viewer/agg-viewer.vue'),
        meta: {
            title: 'Aggregation Viewer',
            productArea: PRODUCT_AREAS.INTERNAL,
            subProductArea: SUB_PRODUCT_AREAS.PENDO_TOOLS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Aggregations viewer'),
                    params: {},
                    icon: { type: 'settings' },
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasJobsAndAggsRedirect(),
        },
    },
    {
        path: '/super/aggregationbuilder',
        name: 'AggregationsBuilder',
        component: () => import('@/agg-builder/aggs-page.vue'),
        meta: {
            title: 'Aggregations builder',
            productArea: PRODUCT_AREAS.INTERNAL,
            subProductArea: SUB_PRODUCT_AREAS.PENDO_TOOLS,
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Aggregations builder'),
                    params: {},
                    icon: { type: 'settings' },
                },
            ],
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
        },
    },
    {
        path: '/super/aggregationbuilder/:tab',
        name: 'AggregationsBuilderTab',
        component: () => import('@/agg-builder/aggs-page.vue'),
        meta: {
            title: 'Aggregations builder',
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperAccessRedirect(),
            productArea: PRODUCT_AREAS.INTERNAL,
            subProductArea: SUB_PRODUCT_AREAS.PENDO_TOOLS,
        },
    },
    {
        path: '/super/tools',
        name: 'SuperTools',
        component: () => import('@/super-tools/super-tools.vue'),
        meta: {
            title: 'Super Tools',
            selectedNavKey: 'pendo-tools',
            productArea: PRODUCT_AREAS.INTERNAL,
            subProductArea: SUB_PRODUCT_AREAS.PENDO_TOOLS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Super Tools'),
                    params: {},
                    icon: { type: 'settings' },
                },
            ],
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperToolsRedirect(),
        },
    },
    {
        path: '/super/tools/id-remap',
        name: 'SuperToolsIdRemap',
        component: () => import('@/super-tools/components/id-remap/id-remap.vue'),
        meta: {
            title: 'ID Remap',
            productArea: PRODUCT_AREAS.INTERNAL,
            subProductArea: SUB_PRODUCT_AREAS.PENDO_TOOLS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Id remap'),
                    params: {},
                    icon: { type: 'users' },
                },
            ],
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperToolsRedirect(),
        },
    },
    {
        path: '/super/tools/migration',
        name: 'SuperToolsMigration',
        component: () => import('@/super-tools/components/migration/migration.vue'),
        meta: {
            title: 'Migration',
            productArea: PRODUCT_AREAS.INTERNAL,
            subProductArea: SUB_PRODUCT_AREAS.PENDO_TOOLS,
            activeNavOption: {
                topOptionName: TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS,
            },
            navigationQuickLinks: [
                {
                    label: ($t) => $t('jumpTo.Migration'),
                    params: {},
                    icon: { type: 'copy' },
                },
            ],
            hasPermissionsDeniedRedirect: () => RoutePermissions.hasSuperToolsMigrationRedirect(),
        },
    },
    {
        path: '/404',
        name: 'NotFound',
        component: () => import('@/errors/404.vue'),
        meta: {
            title: 'Not Found',
            skipLoginRedirect: true,
        },
    },
    {
        path: '/:pathMatch(.*)*',
        redirect: (to) => {
            const alreadyHasSubId = extractSubIdFromPath(to.fullPath);
            if (!alreadyHasSubId && !to.redirectedFrom) {
                // Remove all leading slashes from the path, in the rare case that there are two.
                const trimmedPath = to.path.replace(/^\/+/, '');

                return `/s/${store.state.subscription?.active?.id}/${trimmedPath}`;
            }

            if (store.getters['auth/isAuthenticated']) {
                return { name: 'NotFound' };
            }

            return { name: 'Login' };
        },
    },
];

export const allRoutes = [
    {
        name: 'Base',
        path: '/',
        meta: {
            skipLoginRedirect: true,
        },
        redirect: (to) => {
            return {
                name: 'Login',
                query: {
                    ...to.query,
                },
            };
        },
    },
    {
        path: '/s/:sub',
        component: () => import('@/vue-router/sub-route-layout.vue'),
        children: [...subIdRoutes, ...redirectLegacyRoutesWithSubId],
    },
    ...globalRoutes,
    ...redirectLegacyRoutesGlobal,
];

const router = createRouter({
    routes: allRoutes,
    history: createWebHistory(),
    scrollBehavior (to, from) {
        if (to.path === from.path) {
            return false;
        }

        // `pendo-page-container` class is defined in component library
        // for the `<pendo-page>` component.
        // The class is referenced in this repository,
        // but never directly defined here.
        const scrollElement = document.querySelector('.pendo-page-container');
        if (scrollElement) {
            scrollElement.scrollTo({ top: 0, behavior: 'instant' });
        }

        // Fallback in case the page body was somehow scrolled.
        return { top: 0 };
    },
});

initTitleContext(router);

export { setTitleContext } from '@/vue-router/title-context.js';

let segmentFlagsLoaded = false;

// only add route when running cypress
if (window.Cypress) {
    segmentFlagsLoaded = true;

    router.addRoute({
        path: '/cypress',
        name: 'Cypress Landing Page',
        component: () => import('@/test/cypress-landing-page.vue'),
    });
}

router.beforeEach(async (to, from) => {
    const sub = getActiveSubscription();
    const subId = sub?.id;

    if (subId) {
        if (!localSubscriptionFlagsExist()) {
            // For idle multi-tab scenarios
            setSubscriptionFlags(sub.featureFlags, subId);
        }
    }

    if (to.name !== from.name) {
        cancelPendingAggregations();
    }

    if (to.params?.sub === 'undefined') {
        if (!subId) {
            return true; // handled in app.vue
        }

        return {
            ...to,
            params: {
                ...to.params,
                sub: subId,
            },
        };
    }

    const missingQueries = persistedQueries.filter((query) => from.query[query] && !to.query[query]);

    // If we aren't persisting query params we should be, add them to the route object
    // and restart the beforeEach loop
    if (missingQueries.length) {
        const combinedQueries = missingQueries.reduce((acc, queryKey) => {
            acc[queryKey] = from.query[queryKey];

            return acc;
        }, to.query);

        return {
            ...to,
            query: combinedQueries,
        };
    }

    if (store.state.auth.subAccessError) {
        store.commit('auth/setSubAccessError', { error: null });

        return { name: 'NotFound', query: to.query };
    }

    if (!segmentFlagsLoaded) {
        await store.dispatch('auth/waitForSegmentFlags');
        segmentFlagsLoaded = true;
    }

    if (to.meta.hasPermissionsDeniedRedirect) {
        const failedPermissionsCheckRedirect = to.meta.hasPermissionsDeniedRedirect(to);
        if (failedPermissionsCheckRedirect) {
            return {
                ...failedPermissionsCheckRedirect,
                params: {
                    ...failedPermissionsCheckRedirect.params,
                    sub: to?.params?.sub,
                },
            };
        }
    }

    return true;
});

router.afterEach((to, from) => {
    if (to.name !== from.name || to.path !== from.path) {
        clearTitleContext();
    }
    scheduleApplyTitle();
});

export function switchSubscriptionToRouteName (name, { ignoreQuery = false } = {}) {
    return function switchSubscriptionToRoute ({ params, query }) {
        if (ignoreQuery) {
            const versionQueryParam = query.version;
            const localeQueryParam = query.locale;
            query = {};
            // preserve the ?version=me-app-12345 param used for FE lookasides
            if (versionQueryParam) {
                query.version = versionQueryParam;
            }
            if (localeQueryParam) {
                query.locale = localeQueryParam;
            }
        }

        return { name, params, query };
    };
}

export async function switchSubscription (subId, { shouldNavigate = true } = {}) {
    const oldSubId = getActiveSubscription()?.id;
    try {
        await updateActiveSubscription(subId);
    } catch (err) {
        PendoNotification({
            type: 'error',
            title: 'Failed to change subscription',
            message: err?.response?.data || 'An error occurred while trying to change your subscription',
            duration: 10000,
        });

        return;
    }

    clearLocalFlags(`${SUBSCRIPTION_FLAG_STORAGE_KEY}_${oldSubId}`);

    if (shouldNavigate) {
        const { name, params, query, meta } = router.currentRoute.value;
        const { switchSubscriptionToRoute = identity } = meta;

        const route = switchSubscriptionToRoute({
            name,
            params: { ...params, sub: subId },
            query,
        });

        const newUrl = router.resolve(route);
        window.location.assign(newUrl.href);
    }
}

/* Return href with injected subId if needed; useful for calling $router.push from
 * routes that do not already have a sub id param. Otherwise, you can just use
 * $router.resolve(...).href */
export function toPath (route, config = {}) {
    const resolveRoute = (route, { param = 'href' }) => {
        const to = router.resolve(route);

        return to[param];
    };

    try {
        return resolveRoute(route, config);
    } catch (err) {
        const alreadyHasSubId = route.params?.sub;

        if (!alreadyHasSubId) {
            const subId = getActiveSubscription()?.id;
            const params = route.params || {};
            params.sub = subId;

            try {
                return resolveRoute(
                    {
                        ...route,
                        params,
                    },
                    config,
                );
            } catch (err) {
                throw err;
            }
        }

        throw err;
    }
}

router.pendo = {
    switchSubscription,
    toPath,
};

export default router;
